import type { Sanasto } from './fi';

/**
 * Englanninkielinen sanasto.
 *
 * Tyyppi tulee suomen sanastosta, joten kääntäjä huomauttaa puuttuvasta
 * avaimesta — käännös ei voi jäädä vajaaksi huomaamatta.
 *
 * Käännös ei ole sanasta sanaan vaan samaa asiaa samalla sävyllä: lyhyet
 * napit lyhyinä, selitteet selittävinä. Tuotenimiä (Devikone, Microsoft,
 * Entra ID) ja tietokannan omia tekstejä ei käännetä.
 */
export const en: Sanasto = {
  // --- Yleiset ------------------------------------------------------------
  'yleinen.ladataan': 'Loading…',
  'yleinen.haetaan': 'Searching…',
  'yleinen.tallenna': 'Save',
  'yleinen.tallennetaan': 'Saving…',
  'yleinen.tallennettu': 'Saved.',
  'yleinen.peruuta': 'Cancel',
  'yleinen.sulje': 'Close',
  'yleinen.muokkaa': 'Edit',
  'yleinen.poista': 'Delete',
  'yleinen.valitse': 'Select…',
  'yleinen.valinnainen': '(optional)',
  'yleinen.eiOsumia': 'No matches.',
  'yleinen.eiKirjauksia': 'No entries.',
  'yleinen.tallennusEpaonnistui': 'Saving failed.',
  'yleinen.yhteysEpaonnistui': 'Could not reach the server. Please try again.',
  'yleinen.koodi': 'Code',
  'yleinen.nimi': 'Name',
  'yleinen.tila': 'Status',
  'yleinen.rooli': 'Role',
  'yleinen.sahkoposti': 'Email',
  'yleinen.asiakas': 'Customer',
  'yleinen.tunnit': 'Hours',
  'yleinen.kirjauksia': 'Entries',
  'yleinen.laskutettava': 'Billable',
  'yleinen.eiLaskuteta': 'Not billed',
  'yleinen.eiHintaa': 'no rate',
  'yleinen.aktiivinen': 'Active',
  'yleinen.perustelu': 'Reason',
  'yleinen.kylla': 'yes',
  'yleinen.ei': 'no',
  'yleinen.sisallytaSisainen': 'Include internal work',
  'yleinen.sisainenSelite': '— holidays, absences and internal development',
  'yleinen.vieCsv': 'Export CSV',
  'yleinen.vieJson': 'Export JSON',
  'yleinen.vieExcel': 'Export Excel',

  // --- Roolit -------------------------------------------------------------
  'rooli.ADMIN': 'Administrator',
  'rooli.USER': 'User',
  'rooli.ACCOUNT_MANAGER': 'Account manager',
  'rooli.REPORTER': 'Reporter',

  // --- Kehys ja navigaatio -------------------------------------------------
  'sovellus.nimi': 'Devikone Platform',
  'navi.paanavigaatio': 'Main navigation',
  'navi.kalenteri': 'Calendar',
  'navi.omatTunnit': 'My hours',
  'navi.raportit': 'Reports',
  'navi.vienti': 'Export',
  'navi.taulut': 'Boards',
  'navi.hallinta': 'Administration',
  'navi.kuukaudet': 'Months',
  'navi.agentti': 'Agent and MCP',
  'navi.ulkoasu': 'Appearance',
  'navi.kirjauduUlos': 'Sign out',
  'kehys.vastuuasiakkaasi': 'Your customers',
  'kehys.vastuuasiakkaatSelite':
    'Report figures are the total for these customers, not for the whole company.',

  // --- Kirjautuminen ------------------------------------------------------
  'kirjautuminen.iskulause': 'Every hour to the right customer',
  'kirjautuminen.kuvaus': 'Time tracking, projects, training and skills in one system.',
  'kirjautuminen.microsoft': 'Sign in with Microsoft',
  'kirjautuminen.ohje':
    'Signing in uses your Devikone Microsoft account. If you cannot get in, contact your administrator.',
  'kirjautuminen.virheKeskeytyi': 'Signing in was interrupted. Please try again.',
  'kirjautuminen.virheVanhentui':
    'The sign-in expired on the way. Start again — this happens when the tab has been open for a long time.',
  'kirjautuminen.virheEpaonnistui':
    'Signing in failed. If the problem persists, contact your administrator.',
  'kirjautuminen.virheIstuntoPaattyi': 'Your session has ended. Sign in again to continue.',
  'kirjautuminen.virheYleinen': 'Signing in failed.',
  'kirjautuminen.salasanaOtsikko': 'Sign in with a password',
  'kirjautuminen.salasana': 'Password',
  'kirjautuminen.kirjaudu': 'Sign in',
  'kirjautuminen.kirjaudutaan': 'Signing in…',
  'kirjautuminen.taiMicrosoft': 'or',
  'kirjautuminen.paikallinenOhje':
    'A local account is created by an administrator. It links to your Microsoft account by itself the first time you sign in with Microsoft using the same email address.',
  'kehityskirjautuminen.otsikko': 'Development sign-in',
  'kehityskirjautuminen.selite': 'Bypasses Entra ID. Shown only on the development server.',
  'kehityskirjautuminen.kirjaudu': 'Sign in with development credentials',
  'kehityskirjautuminen.kirjaudutaan': 'Signing in…',
  'kehityskirjautuminen.virhe': 'Development sign-in failed.',

  // --- Salasana -----------------------------------------------------------
  'salasana.vaihda': 'Change password',
  'salasana.nykyinen': 'Current password',
  'salasana.uusi': 'New password',
  'salasana.uusiUudelleen': 'New password again',
  'salasana.eiTasmaa': 'The passwords do not match.',
  'salasana.vaatimus': 'At least 12 characters.',
  'salasana.vaihdettu': 'Password changed. Other sessions have been signed out.',
  'salasana.pakkoOtsikko': 'Change your password to continue',
  'salasana.pakkoSelite':
    'A temporary password has been set for your account. Replace it with your own before continuing.',

  // --- Sivua ei löytynyt --------------------------------------------------
  'eiLoytynyt.otsikko': 'Page not found',
  'eiLoytynyt.selite':
    'The address does not match any view. Check the link or go back to the calendar.',
  'eiLoytynyt.linkki': 'Back to the calendar',

  // --- Asiakasvalitsin ----------------------------------------------------
  'asiakasValitsin.hae': 'Search customers by code or name…',
  'asiakasValitsin.vaihda': 'Change',
  'asiakasValitsin.vastuullasi': 'yours',

  // --- Kestovalitsin ------------------------------------------------------
  'kesto.vahenna': 'Subtract half an hour',
  'kesto.lisaa': 'Add half an hour',

  // --- Jaksovalitsin ------------------------------------------------------
  'jakso.kuukausi': 'Month',
  'jakso.edellinenKuukausi': 'Previous month',
  'jakso.seuraavaKuukausi': 'Next month',
  'jakso.tamaKk': 'This month',
  'jakso.aikavali': 'Date range',
  'jakso.alkupaiva': 'Start date',
  'jakso.loppupaiva': 'End date',
  'jakso.kuluvaKk': 'Current month',
  'jakso.edellinenKk': 'Previous month',
  'jakso.kuluvaNeljannes': 'Current quarter',
  'jakso.vuosi': 'Year',

  // --- Budjettitila -------------------------------------------------------
  'budjetti.HOURS_NEAR_BUDGET': 'Hour budget nearly used',
  'budjetti.VALUE_NEAR_BUDGET': 'Euro budget nearly used',
  'budjetti.HOURS_OVER_BUDGET': 'Hour budget exceeded',
  'budjetti.VALUE_OVER_BUDGET': 'Euro budget exceeded',
  'budjetti.HOURS_AHEAD_OF_SCHEDULE': 'Hours are being used faster than planned',
  'budjetti.VALUE_AHEAD_OF_SCHEDULE': 'Euros are being used faster than planned',
  'budjetti.SCHEDULE_ENDED': 'The schedule has ended, the project is still running',
  'budjetti.ylitysTiivis': 'Over',
  'budjetti.ylitys': 'Budget exceeded',
  'budjetti.varoitus': 'Warning',
  'budjetti.aikataulussa': 'On schedule',

  // --- Kirjauslomake ------------------------------------------------------
  'kirjaus.uusi': 'New entry',
  'kirjaus.muokkaa': 'Edit entry',
  'kirjaus.viimeksiKaytetyt': 'Recently used',
  'kirjaus.paiva': 'Date',
  'kirjaus.tehtavalaji': 'Task type',
  'kirjaus.valitseEnsinAsiakas': 'Select a customer first',
  'kirjaus.eiLaskuteta': ' (not billed)',
  'kirjaus.projekti': 'Project',
  'kirjaus.eiProjektia': 'No project',
  'kirjaus.kesto': 'Duration',
  'kirjaus.sisainenEiLaskuteta': ' — internal work is not billed',
  'kirjaus.kuvaus': 'Description',
  'kirjaus.tehtava': 'Task',
  'kirjaus.poistaTehtavaviite': 'Remove the link to the task',
  'kirjaus.tallennaJaJatka': 'Save and +',
  'kirjaus.valitseAsiakasJaTehtavalaji': 'Select a customer and a task type.',
  'kirjaus.tallennettu': 'Entry saved.',
  'kirjaus.budjetti': (budjetti: string) => `Budget ${budjetti}`,
  'kirjaus.budjettiHtp': (htp: string) => ` (${htp})`,
  'kirjaus.kaytetty': (kaytetty: string, osuus: string) => `used ${kaytetty} · ${osuus}`,

  // --- Kirjauksen tiedot --------------------------------------------------
  'tiedot.otsikko': 'Entry',
  'tiedot.paiva': 'Day',
  'tiedot.kirjaaja': 'Logged by',
  'tiedot.eiKuvausta': 'No description.',
  'tiedot.kirjattu': 'Created',
  'tiedot.muokattu': 'Last edited',
  'tiedot.lukittu': 'The month is locked, so this entry can no longer be changed.',

  // --- Kalenteri ----------------------------------------------------------
  'kalenteri.viikko': 'Week',
  'kalenteri.kuukausi': 'Month',
  'kalenteri.viikkoNumero': (numero: number) => `Week ${numero}`,
  'kalenteri.edellinenViikko': 'Previous week',
  'kalenteri.seuraavaViikko': 'Next week',
  'kalenteri.edellinenKuukausi': 'Previous month',
  'kalenteri.seuraavaKuukausi': 'Next month',
  'kalenteri.tanaan': 'Today',
  'kalenteri.viikonTunnit': 'Hours this week',
  'kalenteri.kuukaudenTunnit': 'Hours this month',
  'kalenteri.tayttoaste': (prosenttia: number) => `Fill rate ${prosenttia} percent`,
  'kalenteri.lukittu': (kuukaudet: string) => `Locked: ${kuukaudet}`,
  'kalenteri.kopioitu': (maara: number) =>
    maara === 1 ? 'Copied 1 entry.' : `Copied ${maara} entries.`,
  'kalenteri.kopiointiEpaonnistui': 'Copying failed.',
  'kalenteri.poistetaanko': 'Delete this entry?',
  'kalenteri.vk': 'Wk',
  'kalenteri.yhteensa': 'Total',
  'kalenteri.muuta': (maara: number) => `+${maara} more`,
  'kalenteri.eiKirjauksia': 'No entries',
  'kalenteri.siirretty': (pvm: string) => `Entry moved to ${pvm}.`,
  'kalenteri.siirtoEpaonnistui': 'Moving the entry failed.',
  'kalenteri.kopioiEdellinen': 'Copy the previous day',
  'kalenteri.kuukausiLukittu': 'The month is locked.',
  'kalenteri.lisaaKirjaus': (paiva: string) => `Add an entry for ${paiva}`,
  'kalenteri.avaaKirjaus': 'Show entry details',
  'kalenteri.avaaEhdotus': (asiakas: string) => `Review machine suggestion: ${asiakas}`,
  'kalenteri.vahvistaEhdotus': 'Suggestion — confirm',
  'kalenteri.ehdotuksia': (maara: number) =>
    maara === 1 ? '1 suggestion' : `${maara} suggestions`,
  'kalenteri.monistaKirjaus': 'Duplicate the entry on the same day',
  'kalenteri.monistettu': (pvm: string) => `Entry duplicated to ${pvm}.`,
  'kalenteri.monistusEpaonnistui': 'Duplicating failed.',
  'kalenteri.muokkaaKirjausta': 'Edit entry',
  'kalenteri.poistaKirjaus': 'Delete entry',
  'kalenteri.ohjeAlku':
    'Browse with the ← and → arrow keys, return to today with T, switch the view with V and K, add an entry for the selected day with N. The sign',
  'kalenteri.ohjeLoppu':
    'means the entry is not billed. Clicking an entry opens its details. Dragging moves an entry to another day; holding Ctrl, Alt or Cmd duplicates it instead of moving it.',
  'kalenteri.ohjeKuukausi':
    ' The month view is for review — clicking a day opens it in the week view.',

  // --- Omat tunnit --------------------------------------------------------
  'omatTunnit.otsikko': 'My hours',
  'omatTunnit.kirjatutTunnit': 'Hours logged',
  'omatTunnit.odotus': 'Expected',
  'omatTunnit.odotusSelite': 'working days × weekly hours / 5',
  'omatTunnit.tayttoaste': 'Fill rate',
  'omatTunnit.tayttoasteSelite': 'are the entries done',
  'omatTunnit.laskutettavienOsuus': 'Billable share',
  'omatTunnit.laskutettavienSelite': 'where the time goes',
  'omatTunnit.asiakkaittain': 'By customer',
  'omatTunnit.tehtavalajeittain': 'By task type',
  'omatTunnit.alaviite':
    'The expected figure ignores public holidays and leave, so a low fill rate during a holiday month is to be expected. The figure is for your own tracking, not a target you are measured against.',

  // --- Raportit -----------------------------------------------------------
  'raportit.otsikko': 'Reports',
  'raportit.naetVastuuasiakkaasi': 'You see your customers',
  'raportit.vastuuasiakkaatSelite':
    'The figures are the total for these customers, not for the whole company.',
  'raportit.laskutettavatTunnit': 'Billable hours',
  'raportit.laskutus': 'Billing',
  'raportit.kirjatutTunnit': 'Hours logged',
  'raportit.laskutusaste': 'Billable rate',
  'raportit.kirjatut': 'Logged',
  'raportit.laskutettavat': 'Billable',
  'raportit.osuus': 'Share',
  'raportit.tarkistettuLyhenne': 'Chk.',
  'raportit.eiKirjauksiaJaksolla': 'No entries in this period.',
  'raportit.yhteensaAsiakkaat': (maara: number) =>
    maara === 1 ? 'Total (1 customer)' : `Total (${maara} customers)`,
  'raportit.kuukausiLukittu': 'Month locked',
  'raportit.kuukausiAvoin': 'Month open — the figures may still change',
  'raportit.kuukausiaLukittu': (lukitut: number, kaikki: number) =>
    `${lukitut}/${kaikki} months locked`,
  'raportit.tarkistettu': 'Checked',
  'raportit.muuttunut': 'Changed',
  'raportit.tarkistamatta': 'Not checked',
  'raportit.tarkistettuOtsikko': (aika: string, kuka: string, muuttunut: boolean) =>
    `Checked ${aika}${kuka ? ` (${kuka})` : ''}${
      muuttunut ? ' — entries have been changed since' : ''
    }`,

  // --- Asiakkaan raportti -------------------------------------------------
  'raporttiAsiakas.tyontekija': 'Employee',
  'raporttiAsiakas.tehtavalaji': 'Task type',
  'raporttiAsiakas.projekti': 'Project',
  'raporttiAsiakas.summa': (kirjatut: string, laskutettavat: string) =>
    `Logged ${kirjatut} · Billable ${laskutettavat}`,
  'raporttiAsiakas.summaLaskutus': (arvo: string) => ` · Billing ${arvo}`,
  'raporttiAsiakas.vieAsiakas': 'Export this customer',
  'raporttiAsiakas.vientiOtsikko': 'Export customer hours',
  'raporttiAsiakas.vientiSelite':
    'The Excel file is grouped like the report: each group starts with a colour-highlighted subheading and ends with a subtotal. CSV is a flat list for further processing.',
  'raporttiAsiakas.sarakkeet': 'Columns',
  'raporttiAsiakas.ryhmittely': 'Group by:',
  'raporttiAsiakas.pvm': 'Date',
  'raporttiAsiakas.arvo': 'Value',
  'raporttiAsiakas.tarkistaUudelleen': 'Check again',
  'raporttiAsiakas.merkitseTarkistetuksi': 'Mark as checked',
  'raporttiAsiakas.peruKuittaus': 'Undo the check',
  'raporttiAsiakas.kuittausEpaonnistui': 'Marking as checked failed.',

  // --- Vienti -------------------------------------------------------------
  'vienti.otsikko': 'Export',
  'vienti.asiakasvastaavaHuomio':
    'The export contains only rows for the customers you are responsible for, and the file is marked as limited both in its name and in its metadata.',
  'vienti.jakso': 'Period',
  'vienti.kuukausi': 'Month',
  'vienti.tamaKuukausi': 'This month',
  'vienti.vapaaAikavali': 'Custom date range',
  'vienti.asiakasRajaus': '(optional filter)',
  'vienti.poistaRajaus': 'Clear the filter',
  'vienti.vainLaskutettavat': 'Billable rows only',
  'vienti.csvMurre': 'CSV dialect',
  'vienti.murreExcel': 'Excel (Finnish) — semicolon, decimal comma, BOM',
  'vienti.murreRfc': 'RFC 4180 — comma, decimal point, no BOM',
  'vienti.murreSelite':
    'Finnish Excel opens the default dialect straight into columns. Choose RFC 4180 if the file is read by a machine.',
  'vienti.jarjestysVirhe': 'The start date cannot be after the end date.',
  'vienti.alaviite':
    'Exporting does not require a lock — a period can be exported for review at any time. The JSON states the lock state of every month in its metadata, so the recipient knows whether the data may still change. Deleted entries are not included.',

  // --- Hallinnan kehys ----------------------------------------------------
  'hallinta.osiot': 'Administration sections',
  'hallinta.asiakkaat': 'Customers',
  'hallinta.projektit': 'Projects',
  'hallinta.hinnasto': 'Rates',
  'hallinta.kayttajat': 'Users',
  'hallinta.muutosloki': 'Change log',
  'hallinta.koulutukset': 'Training',
  'hallinta.koulutustilanne': 'Training status',
  'hallinta.kuukaudet': 'Months',
  'hallinta.asetukset': 'Settings',
  'hallinta.attribuutit': 'Attributes',

  // --- Asiakkaat ----------------------------------------------------------
  'asiakkaat.hae': 'Search by code or name…',
  'asiakkaat.naytaPassivoidut': 'Show deactivated',
  'asiakkaat.uusi': 'New customer',
  'asiakkaat.oletustuntihinta': 'Default hourly rate',
  'asiakkaat.projektit': 'Projects',
  'asiakkaat.avaaProjektit': (nimi: string) => `Open the projects of ${nimi}`,
  'asiakkaat.sisainen': 'internal',
  'asiakkaat.passivoitu': 'Deactivated',
  'asiakkaat.oletustuntihintaKentta': (merkki: string) => `Default hourly rate ${merkki}/h`,
  'asiakkaat.oletustuntihintaSelite':
    'Task types inherit this unless they are given a rate of their own.',
  'asiakkaat.luoTehtavalajit': 'Create the default task types',
  'asiakkaat.luoTehtavalajitSelite': 'Without task types no hours can be logged for the customer.',

  // --- Task type template ---------------------------------------------------
  'pohja.otsikko': 'Default task type template',
  'pohja.selite':
    'The template is copied to a customer once, when the customer is created. A task type added to the template later does not appear on existing customers by itself — it is added here.',
  'pohja.ajanTasalla': 'Every customer has the template task types.',
  'pohja.puuttuuMonelta': (asiakkaita: number) =>
    asiakkaita === 1
      ? 'One customer is missing template task types.'
      : `${asiakkaita} customers are missing template task types.`,
  'pohja.valitseKaikki': 'Select all',
  'pohja.tyhjennaValinta': 'Clear selection',
  'pohja.lisaaValituille': (montako: number) => `Add to selected (${montako})`,
  'pohja.lisataan': 'Adding…',
  'pohja.lisatty': (tehtavalajeja: number, asiakkaita: number) =>
    `Added ${tehtavalajeja} task types to ${asiakkaita} customers.`,
  'pohja.eiMuutaVanhoja':
    'Adding does not change existing task types, names or rates. The internal customer is always left out.',
  'pohja.epaonnistui': 'Adding the task types failed.',

  // --- Entry notes (backlog 3.5) ------------------------------------------
  'huomautus.otsikko': 'Notes on your entries',
  'huomautus.selite':
    'An account manager has commented on these entries. The fix is yours to make: a note does not change the entry and does not block anything.',
  'huomautus.kuittaa': 'Mark as handled',
  'huomautus.kuitattu': 'Handled',
  'huomautus.peruKuittaus': 'Undo',
  'huomautus.avaaPaiva': 'Open the day',
  'huomautus.kirjoita': 'Comment on this entry',
  'huomautus.dialogiOtsikko': 'Note on an entry',
  'huomautus.teksti': 'What needs fixing in this entry?',
  'huomautus.vihje':
    'The message is emailed to the person who logged the entry. A note does not change the entry and does not block the month lock — the fix is theirs to make.',
  'huomautus.laheta': 'Send note',
  'huomautus.lahetetaan': 'Sending…',
  'huomautus.lahetetty': 'The note was saved and sent by email.',
  'huomautus.lahetettyEiPostia': 'The note was saved. No email was sent.',
  'huomautus.epaonnistui': 'Saving the note failed.',
  'huomautus.poista': 'Delete note',
  'huomautus.merkki': (montako: number) =>
    montako === 1 ? 'One open note' : `${montako} open notes`,
  'huomautus.eiAvoimia': 'No open notes.',

  // --- Reminder settings --------------------------------------------------
  'muistutukset.otsikko': 'Reminders',
  'muistutukset.selite':
    'Email reminders are sent by the server timer once each morning. Here you choose which reminders are in use and on which days they go out — the time of day comes from the server timer.',
  'muistutukset.eiPostia':
    'Email is not configured on the server (MAILTRAP_API_TOKEN and MAIL_FROM). Reminders will not be sent even when enabled here.',
  'muistutukset.kaytossa': 'In use',
  'muistutukset.eiKaytossa': 'Not in use',
  'muistutukset.viikonpaiva': 'Send on',
  'muistutukset.ikkuna': 'Sent on days of the month',
  'muistutukset.ikkunaAlku': 'No earlier than',
  'muistutukset.ikkunaLoppu': 'No later than',
  'muistutukset.ikkunaSelite':
    'The first run within this range sends the message. A range rather than a single day, so that a missed run — a server that was down, for instance — does not drop the reminder altogether.',
  'muistutukset.lisateksti': 'Your own addition to the message',
  'muistutukset.lisatekstiSelite':
    'Shown at the end of the message, before the signature. The body itself cannot be edited: it carries computed data such as the week number and the unchecked customers.',
  'muistutukset.esikatsele': 'Preview the message',
  'muistutukset.piilotaEsikatselu': 'Hide preview',
  'muistutukset.esikatseluHuomio':
    'The numbers and names in the preview are examples, not real data. Unsaved changes are not shown in the preview.',
  'muistutukset.laheta': 'Send a test message to myself',
  'muistutukset.lahetetaan': 'Sending…',
  'muistutukset.lahetetty': (osoite: string) => `A test message was sent to ${osoite}.`,
  'muistutukset.tallenna': 'Save reminders',
  'muistutukset.tallennettu': 'Reminders saved.',
  'muistutukset.WEEKLY_ENTRIES_MISSING': 'Hours for the week are missing',
  'muistutukset.WEEKLY_ENTRIES_MISSINGSelite':
    'Sent to everyone who logs hours, if there is not a single entry for the current week.',
  'muistutukset.MONTH_UNCHECKED_CUSTOMERS': 'Customers are unchecked',
  'muistutukset.MONTH_UNCHECKED_CUSTOMERSSelite':
    'Sent to an account manager whose customer still has last month\u2019s hours unchecked.',
  'muistutukset.MONTH_NOT_LOCKED': 'The month is not locked',
  'muistutukset.MONTH_NOT_LOCKEDSelite':
    'Sent to admins if the previous month is still open and has entries in it.',
  'muistutukset.TRAINING_OVERDUE': 'Mandatory training is outstanding',
  'muistutukset.TRAINING_OVERDUESelite':
    'Sent to anyone missing a mandatory course or whose completion is about to expire (docs/KOULUTUKSET.md).',
  'muistutukset.TASK_BLOCKED_OVERDUE': 'A task has been blocked past the limit',
  'muistutukset.TASK_BLOCKED_OVERDUESelite':
    'Sent to the task creator and the account manager once a task has sat in BLOCKED for more than eight days, then weekly.',
  'muistutukset.MILESTONE_DUE': 'A milestone is due or overdue',
  'muistutukset.MILESTONE_DUESelite':
    'Sent to the milestone creator and the account manager three days before an unreached milestone is due, and weekly while it is overdue.',
  'muistutukset.DEVELOPMENT_REVIEW_DUE': 'Development reviews are overdue',
  'muistutukset.DEVELOPMENT_REVIEW_DUESelite':
    "Sent to the manager at the start of the month when a report's development review is overdue (docs/KEHITYSSUUNNITELMAT.md).",
  'muistutukset.CERTIFICATION_EXPIRING': 'A certification is expiring',
  'muistutukset.CERTIFICATION_EXPIRINGSelite':
    'Sent to the employee at the start of the month when their certification expires within 90 days.',
  'muistutukset.arkipaivisinSelite':
    'The day is not a setting: the message goes out on the weekday the task crosses the limit, and a weekend day rolls over to Monday.',
  'muistutukset.arkipaivisinMilestoneSelite':
    'The day is not a setting: the advance message goes out three days before the due date, moved back to Friday if that falls on a weekend. The overdue message goes out on the first weekday after the due date and then weekly.',
  'muistutukset.jarjestysVihje':
    'Schedule the check reminder before the lock reminder: the account manager checks, the admin locks.',

  // --- Employee breakdown (12.6) -------------------------------------------
  'tyontekijat.otsikko': 'Breakdown by employee',
  'tyontekijat.tyontekija': 'Employee',
  'tyontekijat.osuus': 'Share of logged',
  'tyontekijat.odotettu': 'Expected',
  'tyontekijat.laskutusaste': 'Billable rate',
  'tyontekijat.kulunut': (alkaen: string, asti: string, arkipaivia: number) =>
    `Figures for ${alkaen}–${asti}: ${arkipaivia} business days elapsed.`,
  'tyontekijat.tavoiteSelite': (tavoite: string) => `Target ${tavoite} unless set individually.`,
  'tyontekijat.tavoite': (tavoite: string) => `target ${tavoite}`,
  'tyontekijat.alleTavoitteen': (tavoite: string) => `Below the ${tavoite} target`,
  'tyontekijat.poissa': (tunnit: string) => `away ${tunnit}`,
  'tyontekijat.eiTyontekijoita': 'No employees are expected to log hours.',
  'tyontekijat.mihinAikaMeni': 'Where the time went',
  'tyontekijat.avaaErittely': (nimi: string) => `Show customers and projects: ${nimi}`,
  'tyontekijat.porautumisSelite':
    'The same figures as the row: elapsed period, internal work included. The customer name opens the per-customer breakdown.',
  'tyontekijat.eiProjektia': 'No project',
  'tyontekijat.eiKirjauksia': 'No entries in the elapsed period.',
  'tyontekijat.avaaAsiakas': (nimi: string) => `Per-customer breakdown: ${nimi}`,
  'tyontekijat.alaviite':
    'The billable rate is billable hours divided by the working time elapsed — it does not improve by logging less. The share of logged hours tells where the logged time went. Holiday and sick leave reduce the expected working time. Internal work is always included in logged hours, even when it is filtered out of the customer table.',

  // --- Billable target (12.6) ----------------------------------------------
  'tavoite.otsikko': 'Billable work target',
  'tavoite.selite':
    'The target is the share of elapsed working time that goes to billable work. It is visible to admins only, in the employee breakdown of the report. An individual target can be set on the user card.',
  'tavoite.kentta': 'Default target',
  'tavoite.kayttajaKentta': 'Individual billable target',
  'tavoite.kayttajaSelite': (oletus: string) =>
    `Empty = the system default (${oletus}). Visible to admins only.`,
  'tavoite.tallenna': 'Save target',
  'tavoite.tallennettu': 'Target saved.',
  'tavoite.virheellinen': 'The target must be between 0 and 100 %.',

  // --- Asiakaskortti ------------------------------------------------------
  'asiakas.hinnanMuutosVahvistus':
    'A rate change applies only to new entries. Hours already logged keep the rate they were logged with.\n\nContinue?',
  'asiakas.eiLoytynyt': 'Customer not found.',
  'asiakas.sisainenSelite': 'internal customer — no billing and no account managers',
  'asiakas.savy': 'Calendar hue',
  'asiakas.savySelite':
    'The hue tells you in the calendar who the work was for. Without a choice it is derived from the customer code, so every customer gets a colour with no upkeep. Pick a hue only when two customers land on the same one and it gets in the way.',
  'asiakas.savyJohdettu': 'Derived from the code',
  'asiakas.savyJohdettuSelite': 'No choice — the hue follows the customer code.',
  'asiakas.savyKaytossa': (asiakkaat: string) => `Also: ${asiakkaat}`,
  'asiakas.savyVapaa': 'No other customer in this hue.',
  'asiakas.savyMuita': (maara: number) => `and ${maara} more`,
  'asiakas.savyValittu': 'Selected hue',
  'savy.punainen': 'Red',
  'savy.oranssi': 'Orange',
  'savy.kulta': 'Gold',
  'savy.vihrea': 'Green',
  'savy.smaragdi': 'Emerald',
  'savy.turkoosi': 'Turquoise',
  'savy.taivas': 'Sky',
  'savy.violetti': 'Violet',
  'savy.purppura': 'Purple',
  'savy.pinkki': 'Pink',
  'asiakas.perustiedot': 'Basic details',
  'asiakas.aktiivinenSelite': ' — hours cannot be logged for a deactivated customer',
  'asiakas.asiakasvastaavat': 'Account managers',
  'asiakas.eiAsiakasvastaavaa':
    'No account manager. At the turn of the month nobody reviews this customer’s hours.',
  'asiakas.asiakasvastaavaSelite':
    'An account manager sees every time entry and every amount for this customer.',
  'asiakas.tehtavalajit': 'Task types',
  'asiakas.uusiTehtavalaji': 'New task type',
  'asiakas.tehtavalajiOtsikko': (nimi: string) => `Task type · ${nimi}`,
  'asiakas.tuntihinta': 'Hourly rate',
  'asiakas.poistettuKaytosta': 'disabled',
  'asiakas.asiakkaanOletus': '(customer default)',
  'asiakas.laskutettavaOletuksena': 'Billable by default',
  'asiakas.tuntihintaKentta': (merkki: string) => `Hourly rate ${merkki}/h`,
  'asiakas.periAsiakkaalta': '(empty = inherit from the customer)',
  'asiakas.kaytossa': 'In use',

  // --- Projektit ----------------------------------------------------------
  'projektit.PLANNED': 'Planned',
  'projektit.ACTIVE': 'Running',
  'projektit.CLOSED': 'Closed',
  'projektit.uusi': 'New project',
  'projektit.eiSisaiselle': 'Projects are not created for the internal customer.',
  'projektit.valitseAsiakas': 'Select a customer to see its projects.',
  'projektit.aikataulu': 'Schedule',
  'projektit.budjetti': 'Budget',
  'projektit.kaytetty': 'Used',
  'projektit.eiProjekteja': 'No projects.',
  'projektit.ajasta': (osuus: string) => ` / ${osuus} of the time`,
  'projektit.otsikko': (nimi: string) => `Project · ${nimi}`,
  'projektit.uusiOtsikko': (asiakas: string) => `New project · ${asiakas}`,
  'projektit.alkaa': 'Starts',
  'projektit.paattyy': 'Ends',
  'projektit.tuntihinta': (merkki: string) => `Hourly rate ${merkki}/h`,
  'projektit.tuntihintaSelite':
    'Overrides the task type and customer rates. When empty, the rate comes from the task type.',
  'projektit.periTehtavalajilta': 'inherit from the task type',
  'projektit.kokonaisbudjetti': 'Total budget',
  'projektit.arvo': 'Value',
  'projektit.htpVihje': (htp: string, kerroin: string) => `= ${htp} pd (${kerroin} h/pd)`,
  'projektit.arvoVihje': (tunnit: string, hinta: string, merkki: string, summa: string) =>
    `Hint: ${tunnit} h × ${hinta} ${merkki}/h = ${summa}`,
  'projektit.htpMuunnos': 'Person-day conversion h/pd',
  'projektit.htpMuunnosSelite': (oletus: string) => `(empty = default from settings ${oletus})`,

  // --- Projektin seuranta -------------------------------------------------
  'projekti.tunnit': 'Hours',
  'projekti.tunnitSelite': 'All hours logged to the project, including non-billable ones',
  'projekti.arvo': 'Value',
  'projekti.arvoSelite': 'The value of billable entries only',
  'projekti.arvoEiNakyvissa':
    'The value budget is not visible to you. The hour budget above is planning information and is shown to everyone.',
  'projekti.eiAikataulua': 'The project has no schedule.',
  'projekti.aikataulu': (alkaa: string, paattyy: string) => `Schedule ${alkaa} – ${paattyy}`,
  'projekti.kulunut': (osuus: string) => `${osuus} elapsed`,
  'projekti.eiBudjettia': (kaytetty: string) => `No budget has been set. Used ${kaytetty}.`,
  'projekti.kaytettyOsuus': (osuus: string) => `${osuus} used`,
  'projekti.jaljella': (maara: string) => `, ${maara} left`,
  'projekti.aikataulustaKulunut': (osuus: string) => ` · ${osuus} of the schedule elapsed`,
  'projekti.aikataulumerkki': (osuus: string) => `${osuus} of the schedule elapsed`,
  'projekti.htpLisa': (kaytetty: string, budjetti: string, kerroin: string) =>
    `${kaytetty} / ${budjetti} · 1 pd = ${kerroin} h`,
  'projekti.laskutettavienOsuus': (osuus: string) => `Billable share of hours ${osuus}`,
  'projekti.tehtavalajeittain': 'By task type',
  'projekti.tyontekijoittain': 'By employee',
  'projekti.kertyma': 'Cumulative by month',
  'projekti.budjettimerkki': 'Budget',

  // --- Hinnasto -----------------------------------------------------------
  'hinnasto.otsikko': 'Rates',
  'hinnasto.takautuva': 'Retroactive rate calculation',
  'hinnasto.hinnoittelemattomatAlku': 'Billable entries without a rate:',
  'hinnasto.hinnoittelemattomatLoppu': (asiakkaat: number) =>
    `/ ${asiakkaat} customers. These will not end up on an invoice, and they block locking the month without a separate acknowledgement.`,
  'hinnasto.oletushinta': 'Default rate',
  'hinnasto.vaihteluvali': 'Range',
  'hinnasto.omatHinnat': 'Own rates',
  'hinnasto.omatHinnatArvo': (tehtavalajit: number, projektit: number) =>
    `${tehtavalajit} task types · ${projektit} projects`,
  'hinnasto.hinnoittelematta': 'Without a rate',
  'hinnasto.ajoSelite':
    'Recalculates entry rates with the current price list. Applies to billable entries only; non-billable ones stay without a rate. A locked month rejects the run.',
  'hinnasto.alkaen': 'From',
  'hinnasto.asti': 'To',
  'hinnasto.perusteluSelite':
    'Required, at least 10 characters. Stored in the change log together with the impact summary.',
  'hinnasto.perusteluEsimerkki':
    'E.g. Framework agreement rate revision, retroactive from 1 Jan 2026',
  'hinnasto.ajoTehty': 'Run completed',
  'hinnasto.kuivaharjoitus': 'Dry run',
  'hinnasto.muuttuu': 'Changing',
  'hinnasto.erotus': 'Difference',
  'hinnasto.ennen': 'Before',
  'hinnasto.jalkeen': 'After',
  'hinnasto.ajaMuutos': 'Run the change',
  'hinnasto.ajoEpaonnistui': 'The run failed.',

  // --- Käyttäjät ----------------------------------------------------------
  'kayttajat.selite':
    'Users are not created by hand: an account is born at the first sign-in with a Microsoft account.',
  'kayttajat.vastuuasiakkaat': 'Assigned customers',
  'kayttajat.passivoitu': 'deactivated',
  'kayttajat.kayttajaOtsikko': (nimi: string) => `User · ${nimi}`,
  'kayttajat.vastuuOtsikko': (nimi: string) => `Assigned customers · ${nimi}`,
  'kayttajat.asiakasvastaavaSelite':
    'An account manager sees the hours and amounts of their assigned customers only. Assign the customers separately — without them they see nothing.',
  'kayttajat.aktiivinenSelite': ' — deactivating ends the sessions immediately',
  'kayttajat.eiVastuuasiakkaita': 'No assigned customers.',
  'kayttajat.lisaaAsiakas': 'Add a customer by searching…',
  'kayttajat.poistaAsiakas': (nimi: string) => `Remove ${nimi}`,
  'kayttajat.kirjautuminen': 'Sign-in',
  'kayttajat.microsoftTili': 'Microsoft account',
  'kayttajat.paikallinenTili': 'Local password',
  'kayttajat.eiKirjautumistapaa': 'No sign-in method',
  'kayttajat.eiKirjautumistapaaSelite':
    'Waiting for the first Microsoft sign-in with the same email address.',
  'kayttajat.uusiKayttaja': 'New user',
  'kayttajat.uusiOtsikko': 'New local account',
  'kayttajat.uusiSelite':
    'The account is created immediately. If you set a password, the user can sign in with it and will change it on first sign-in. Without one, the account waits for a Microsoft sign-in.',
  'kayttajat.salasanaOtsikko': (nimi: string) => `Password · ${nimi}`,
  'kayttajat.asetaSalasana': 'Set a password',
  'kayttajat.nollaaSalasana': 'Reset password',
  'kayttajat.salasanaValinnainen': 'Password (optional)',
  'kayttajat.pakotaVaihto': 'The user changes the password on first sign-in',
  'kayttajat.salasanaAsetettu': 'Password set. Deliver it to the user over a safe channel.',
  'kayttajat.poistaSalasana': 'Remove password sign-in',
  'kayttajat.poistaSalasanaVarmistus': (nimi: string) =>
    `Remove password sign-in from ${nimi}? The account remains and will sign in with Microsoft only.`,
  'kayttajat.viikkotunnit': 'Weekly hours target',

  // --- Muutosloki ---------------------------------------------------------
  'muutosloki.otsikko': 'Change log',
  'muutosloki.rajaa': 'Filter by action',
  'muutosloki.kaikkiToiminnot': 'All actions',
  'muutosloki.eiMerkintoja': 'No records.',
  'muutosloki.alaviite':
    'The log records rates, budgets, roles, customer assignments and exports. Time entries are not logged row by row: they carry their own timestamps, and the month lock closes the window for editing them. The lock history is on the Months tab.',
  'muutosloki.CUSTOMER_CREATE': 'Customer created',
  'muutosloki.CUSTOMER_UPDATE': 'Customer changed',
  'muutosloki.TASK_TYPE_CREATE': 'Task type created',
  'muutosloki.TASK_TYPE_UPDATE': 'Task type changed',
  'muutosloki.TASK_TYPE_TEMPLATE_APPLY': 'Task type template applied',
  'muutosloki.PROJECT_CREATE': 'Project created',
  'muutosloki.PROJECT_UPDATE': 'Project changed',
  'muutosloki.ASSIGNMENT_CHANGE': 'Customer assignments changed',
  'muutosloki.CUSTOMER_CONTACT_CREATE': 'Contact added',
  'muutosloki.CUSTOMER_CONTACT_UPDATE': 'Contact changed',
  'muutosloki.CUSTOMER_CONTACT_DELETE': 'Contact deleted',
  'muutosloki.CUSTOMER_LINK_CREATE': 'Document link added',
  'muutosloki.CUSTOMER_LINK_UPDATE': 'Document link changed',
  'muutosloki.CUSTOMER_LINK_DELETE': 'Document link deleted',
  'muutosloki.USER_UPDATE': 'User changed',
  'muutosloki.RATE_RECALCULATE': 'Retroactive rate calculation',
  'muutosloki.SETTINGS_UPDATE': 'System setting change',
  'muutosloki.BRANDING_UPDATE': 'Company logo change',

  // --- Kuukaudet ----------------------------------------------------------
  'kuukaudet.edellinenVuosi': 'Previous year',
  'kuukaudet.seuraavaVuosi': 'Next year',
  'kuukaudet.kuukausi': 'Month',
  'kuukaudet.lukittu': 'Locked',
  'kuukaudet.avoin': 'Open',
  'kuukaudet.avaa': 'Unlock',
  'kuukaudet.lukitse': 'Lock',
  'kuukaudet.historia': 'History',
  'kuukaudet.tapahtumaLukittu': 'locked',
  'kuukaudet.tapahtumaAvattu': 'unlocked',
  'kuukaudet.eiTapahtumia': 'No events for this year.',
  'kuukaudet.lukitseOtsikko': (kuukausi: string) => `Lock ${kuukausi}`,
  'kuukaudet.avaaOtsikko': (kuukausi: string) => `Unlock ${kuukausi}`,
  'kuukaudet.kootaan': 'Building the checklist…',
  'kuukaudet.hinnoittelemattomat': (maara: number) =>
    maara === 1 ? '1 billable entry without a rate' : `${maara} billable entries without a rate`,
  'kuukaudet.hinnoittelemattomatSelite':
    'These will not end up on an invoice. Price the customers or acknowledge deliberately — after locking they cannot be fixed without unlocking the month.',
  'kuukaudet.kappaletta': (maara: number) => (maara === 1 ? '1 entry' : `${maara} entries`),
  'kuukaudet.kuittaanJaLukitsen': 'I acknowledge the entries without a rate and lock anyway',
  'kuukaudet.edellinenAuki': 'The previous month is still open',
  'kuukaudet.edellinenAukiSelite':
    'Locking works anyway, but check whether the previous month is meant to stay open.',
  'kuukaudet.tarkistamatta': (maara: number) =>
    maara === 1 ? '1 customer not checked' : `${maara} customers not checked`,
  'kuukaudet.tarkistamattaSelite':
    'The check is missing, or entries have been changed after it. Locking works anyway — the mark only tells what has been gone through.',
  'kuukaudet.muuttunutKuittauksenJalkeen': ' — changed after the check',
  'kuukaudet.ilmanVastaavaa': (maara: number) =>
    maara === 1
      ? '1 customer without an account manager'
      : `${maara} customers without an account manager`,
  'kuukaudet.ilmanVastaavaaSelite': 'Nobody has reviewed the hours of these customers.',
  'kuukaudet.kirjausta': (maara: number) => (maara === 1 ? '1 entry' : `${maara} entries`),
  'kuukaudet.vajaat': (maara: number) =>
    maara === 1 ? '1 incomplete month' : `${maara} incomplete months`,
  'kuukaudet.ylipitkatPaivat': 'Days with more than 24 hours',
  'kuukaudet.perusteluEsimerkki': 'E.g. August invoiced',
  'kuukaudet.lukitseKuukausi': 'Lock the month',
  'kuukaudet.lukitusEpaonnistui': 'Locking failed.',
  'kuukaudet.avausSelite':
    'Unlocking removes nothing. It allows entries to be added and edited again, and it stays in the log together with the reason.',
  'kuukaudet.miksiAvataan': 'Why is the month being unlocked?',
  'kuukaudet.avausPerusteluEsimerkki': 'E.g. Three entries were missing from the NORDIC project',
  'kuukaudet.vahintaanMerkkia': (merkkeja: number) =>
    `At least ${merkkeja} characters. Shown in the history as written.`,
  'kuukaudet.avaaKuukausi': 'Unlock the month',
  'kuukaudet.avausEpaonnistui': 'Unlocking failed.',

  // --- Järjestelmäasetukset -----------------------------------------------
  'asetukset.otsikko': 'System settings',
  'asetukset.valuutta': 'Currency',
  'asetukset.valuuttaSelite':
    'The currency is a system-wide setting: it applies to every user, customer and project. Others see the change on their next page load.',
  'asetukset.valuuttaVaroitus':
    'Changing it does not convert any numbers. Hourly rates, budgets and the value of hours already logged stay as they are — only the currency beside them changes.',
  'asetukset.valuuttaKentta': 'System currency',
  'asetukset.esimerkki': 'How amounts will look',
  'asetukset.esimerkkiTuntihinta': 'Hourly rate',
  'asetukset.esimerkkiSumma': 'Value of entries',
  'asetukset.tallenna': 'Save currency',
  'asetukset.tallennetaan': 'Saving…',
  'asetukset.tallennettu': 'Currency saved.',
  'asetukset.vahvistus': (valuutta: string) =>
    `The system currency becomes ${valuutta} for every user.\n\nNo numbers are converted: 115.00 stays 115.00, only the currency changes.\n\nContinue?`,

  // --- Ulkoasu ------------------------------------------------------------
  'ulkoasu.otsikko': 'Appearance',
  'ulkoasu.teema': 'Theme',
  'ulkoasu.teemaSelite':
    'The choice is stored in this browser, not in your user account. On another machine or in another browser the theme is chosen separately.',
  'ulkoasu.teemaVaalea': 'Light',
  'ulkoasu.teemaVaaleaSelite': 'Always light, whatever the system does.',
  'ulkoasu.teemaTumma': 'Dark',
  'ulkoasu.teemaTummaSelite': 'Always dark, whatever the system does.',
  'ulkoasu.teemaJarjestelma': 'Follow the system',
  'ulkoasu.teemaJarjestelmaSelite': 'Follows the operating system’s light/dark setting.',
  'ulkoasu.jarjestelmaNytTumma': 'The system is in dark mode right now.',
  'ulkoasu.jarjestelmaNytVaalea': 'The system is in light mode right now.',
  'ulkoasu.kieli': 'Language',
  'ulkoasu.kieliSelite':
    'The choice is stored in this browser just like the theme. Without a choice the browser language is used. Text entered for customers, projects and entries is always shown as it was saved.',
  'ulkoasu.kieliFiSelite': 'The original language of the application.',
  'ulkoasu.kieliEnSelite': 'The user interface in English.',
  'ulkoasu.varit': 'Customer colours',
  'ulkoasu.varitSelite':
    'Every customer gets its own hue in the calendar, on the entry form and in the customer picker. The hue only says who the work was for — billability, locking and notes stay marked as before. The choice is stored in this browser, like the theme.',
  'ulkoasu.varitKaytossa': 'On',
  'ulkoasu.varitKaytossaSelite':
    'The customer code sits on a coloured background and the calendar card gets a coloured stripe.',
  'ulkoasu.varitPois': 'Off',
  'ulkoasu.varitPoisSelite': 'The customer code alone, with no colour. Nothing is hidden.',

  // --- API-tunnukset ------------------------------------------------------
  'tunnukset.otsikko': 'API tokens',
  'tunnukset.selite':
    'A token runs the import from a script without signing in. It is shown only once, when it is created — a lost token is revoked and replaced.',
  'tunnukset.rajaus':
    'A token reaches the import and read endpoints. It cannot unlock months, manage assignments or create more tokens.',
  'tunnukset.nimi': 'Name',
  'tunnukset.nimiEsimerkki': 'e.g. History migration 2026',
  'tunnukset.rooli': 'Role',
  'tunnukset.voimassa': 'Valid (days)',
  'tunnukset.luo': 'Create token',
  'tunnukset.kertaalleen':
    'Copy the token now. It cannot be shown again — the database holds only a hash.',
  'tunnukset.kopioi': 'Copy',
  'tunnukset.kopioitu': 'Copied',
  'tunnukset.piilota': 'I have saved it, hide',
  'tunnukset.tila': 'Status',
  'tunnukset.tila.active': 'Active',
  'tunnukset.tila.expired': 'Expired',
  'tunnukset.tila.revoked': 'Revoked',
  'tunnukset.viimeksi': 'Last used',
  'tunnukset.vanhenee': 'Expires',
  'tunnukset.eiKaytetty': 'Never used',
  'tunnukset.eiTunnuksia': 'No tokens.',
  'tunnukset.peruuta': 'Revoke token',
  'tunnukset.luonut': (nimi: string, pvm: string) => `${nimi} · ${pvm}`,
  'tunnukset.vahvistus': (nimi: string) =>
    `Revoke the token “${nimi}”?\n\nIt stops working immediately, and any script using it starts getting 401.`,
  'muutosloki.IMPORT_RUN': 'Import run',
  'muutosloki.API_TOKEN_CREATE': 'API token created',
  'muutosloki.API_TOKEN_REVOKE': 'API token revoked',
  'muutosloki.EXPORT': 'Export',
  'muutosloki.KANBAN_LANE_CREATE': 'Swimlane created',
  'muutosloki.KANBAN_LANE_UPDATE': 'Swimlane changed',
  'muutosloki.KANBAN_LANE_DELETE': 'Swimlane deleted',
  'muutosloki.KANBAN_TASK_CREATE': 'Task created',
  'muutosloki.KANBAN_TASK_UPDATE': 'Task changed',
  'muutosloki.KANBAN_TASK_MOVE': 'Task moved',
  'muutosloki.KANBAN_TASK_COPY': 'Task duplicated',
  'muutosloki.KANBAN_TASK_DELETE': 'Task deleted',
  'muutosloki.KANBAN_COMMENT_CREATE': 'Comment added',
  'muutosloki.KANBAN_COMMENT_UPDATE': 'Comment edited',
  'muutosloki.KANBAN_COMMENT_DELETE': 'Comment deleted',
  'muutosloki.KANBAN_REMINDER_CREATE': 'Reminder set',
  'muutosloki.KANBAN_REMINDER_UPDATE': 'Reminder changed',
  'muutosloki.KANBAN_REMINDER_DELETE': 'Reminder cancelled',
  'muutosloki.KANBAN_REACTION_ADD': 'Reaction added',
  'muutosloki.KANBAN_REACTION_REMOVE': 'Reaction removed',
  'muutosloki.KANBAN_MILESTONE_CREATE': 'Milestone created',
  'muutosloki.KANBAN_MILESTONE_UPDATE': 'Milestone changed',
  'muutosloki.KANBAN_MILESTONE_DELETE': 'Milestone deleted',
  'muutosloki.KANBAN_TASK_MILESTONE': 'Task milestone changed',
  'muutosloki.PARTNER_CREATE': 'Partner added',
  'muutosloki.PARTNER_UPDATE': 'Partner changed',
  'muutosloki.PARTNER_DELETE': 'Partner deleted',
  'muutosloki.PARTNER_CONTACT_CREATE': 'Partner contact added',
  'muutosloki.PARTNER_CONTACT_UPDATE': 'Partner contact changed',
  'muutosloki.PARTNER_CONTACT_DELETE': 'Partner contact deleted',

  // --- Koneen ehdotukset (backlog 5) --------------------------------------
  'ehdotus.otsikko': 'Machine suggestion',
  'ehdotus.selite':
    'An agent suggested this entry. It is not an entry until you confirm it, and the duration is yours to state — the agent was not allowed to guess it.',
  'ehdotus.perustelu': 'Reasoning',
  'ehdotus.lahde': 'Source',
  'ehdotus.kestoPuuttuu': 'Choose a duration. The suggestion cannot be confirmed without one.',
  'ehdotus.kestoOhje': 'You can still change the duration before confirming.',
  'ehdotus.vahvista': 'Confirm as entry',
  'ehdotus.vahvistettu': 'Suggestion confirmed as an entry.',
  'ehdotus.hylkaa': 'Dismiss',
  'ehdotus.hylatty': 'Suggestion dismissed.',

  // --- Agenttitunnus ja MCP (backlog 5) -----------------------------------
  'agentti.otsikko': 'Agent and MCP',
  'agentti.selite':
    'An agent — Claude Code, for example — can help you remember who and what you worked on. It acts on your behalf with its own token.',
  'agentti.rajatOtsikko': 'What the agent can and cannot do',
  'agentti.rajaEhdotus':
    'It does not log hours. It makes suggestions that appear in the calendar for you to confirm.',
  'agentti.rajaKesto':
    'It does not state the duration. Keyboard time is not billable time, so how long is always yours to say.',
  'agentti.rajaOmat': 'It sees only your own entries, nobody else\u2019s.',
  'agentti.rajaIkkuna': 'It reaches the current and the previous month, plus one week forward.',
  'agentti.rajaRahat': 'It sees no hourly rates and no amounts of money.',
  'agentti.luo': 'Create agent token',
  'agentti.nimiEsimerkki': 'e.g. Claude Code, work laptop',
  'agentti.kertaalleen':
    'Copy the token now. It cannot be shown again — the database holds only a hash.',
  'agentti.asennus': 'Setting up Claude Code',
  'agentti.asennusSelite':
    'Run this command on your machine. It adds the server to Claude Code with this token.',
  'agentti.eiTunnuksia': 'You have no agent tokens.',

  // Esimerkkikehotteet. Nämä ovat sivun konkreettisin osa: rajaukset
  // kertovat mitä agentti ei tee, ja nämä sen, mitä sille sanotaan.
  'agentti.esimerkitOtsikko': 'What to say to the agent',
  'agentti.esimerkitSelite':
    'Type these to Claude Code in the repository where the work was done. The agent looks up customer and project identifiers itself — you do not need to know them.',
  'agentti.esimPaivaOtsikko': 'At the end of the day',
  'agentti.esimPaivaKehote': 'Look at today\u2019s commits and suggest hours.',
  'agentti.esimPaivaSelite':
    'The suggestions appear in your calendar. You review them and give the durations.',
  'agentti.esimUnohtunutOtsikko': 'A day you forgot',
  'agentti.esimUnohtunutKehote': 'What did I do last Thursday? Suggest an entry for that day.',
  'agentti.esimUnohtunutSelite':
    'The agent reads the branch and the commits from that day. Works from the start of the previous month.',
  'agentti.esimProjektiOtsikko': 'Assigning to a project',
  'agentti.esimProjektiKehote':
    'Work in this repository goes to Acme\u2019s PORT26 project. Suggest yesterday\u2019s hours there.',
  'agentti.esimProjektiSelite':
    'Without a project the entry lands on the customer alone, and the project budget tracking stays incomplete.',
  'agentti.esimTarkistusOtsikko': 'Before suggesting',
  'agentti.esimTarkistusKehote': 'What have I already logged this week?',
  'agentti.esimTarkistusSelite':
    'You see what is already logged, so no duplicate suggestions appear.',
  'agentti.esimKuukausiOtsikko': 'At the turn of the month',
  'agentti.esimKuukausiKehote': 'Is August fully logged?',
  'agentti.esimKuukausiSelite':
    'It answers from your own totals and tells you whether the month is already locked.',
  // Boards (PROJEKTINHALLINTA 14). Same token, different workflow.
  'agentti.taulutOtsikko': 'Boards',
  'agentti.taulutSelite':
    'The same token works on the Boards view: the agent reads tasks, picks up the ones assigned to it and writes a work log in your name.',
  'agentti.tauluNakee': 'It sees the same boards you do \u2014 the customers you are a member of.',
  'agentti.tauluSiirto':
    'It moves only tasks assigned to the AI assistant or to you, or unassigned tasks you created.',
  'agentti.tauluDone':
    'It moves a task at most to IN REVIEW. You move it to DONE once you have checked the work.',
  'agentti.tauluJumissa': 'It moves to BLOCKED only with a comment that says what is in the way.',
  'agentti.tauluKommentti':
    'Its comments appear under your name with a robot mark carrying the token name. A comment is not a time entry.',
  'agentti.tauluLuonti':
    'It creates a task only when you ask, into TODO or INFO. The task shows as created by you, with a robot mark.',
  'agentti.tauluMilestonet':
    'It reads the swimlanes’ milestones and attaches tasks to them when you ask. Creating milestones and marking them reached belong to the account manager.',
  'agentti.tauluRaportti':
    'It prepares a customer report when you ask, if you are an account manager or an administrator. The report comes to you; you send it to the customer.',
  'agentti.esimTauluOtsikko': 'What is assigned',
  'agentti.esimTauluKehote':
    'Which tasks are assigned to me or to the AI assistant on the Acme web shop project board?',
  'agentti.esimTauluSelite':
    'The agent fetches the board and lists the tasks with descriptions and comments.',
  'agentti.esimPoimiOtsikko': 'Picking up a task',
  'agentti.esimPoimiKehote':
    'Pick up the task assigned to the AI assistant, do it in this repo and move it to review.',
  'agentti.esimPoimiSelite':
    'The agent moves the task to DOING, does the work and moves it to IN REVIEW with a comment. You check it and move it to DONE.',
  'agentti.esimTyolokiOtsikko': 'Work log on a task',
  'agentti.esimTyolokiKehote':
    'Write a comment on the task about what I did today and where I stopped.',
  'agentti.esimTyolokiSelite':
    'The comment shows on the board with a robot mark. Hours are suggested separately into the calendar.',
  'agentti.esimMilestoneOtsikko': 'Milestone status',
  'agentti.esimMilestoneKehote':
    'Which tasks are still open for the next milestones of Acme’s web shop project, and who owns them?',
  'agentti.esimMilestoneSelite':
    'The agent fetches each swimlane’s next unreached milestone, its due date, logged hours and the open tasks with links.',
  'agentti.esimDeadlineOtsikko': 'Deadlines',
  'agentti.esimDeadlineKehote':
    'Which tasks on Acme’s board are overdue? Set the deadline of "Renew certificate" to Friday.',
  'agentti.esimDeadlineSelite':
    'The agent filters the overdue open tasks and sets a deadline when you ask. Setting one works only with an account manager’s or administrator’s token.',
  'agentti.esimRaporttiOtsikko': 'Customer report',
  'agentti.esimRaporttiKehote':
    'Make an English status report for Acme covering all its boards, with tasks completed in the last two weeks.',
  'agentti.esimRaporttiSelite':
    'The agent returns the same Markdown report as the board’s Customer report button. Read it and send it yourself.',
  'agentti.vinkkiOtsikko': 'Tip:',
  'agentti.vinkkiTeksti':
    'write in the repository\u2019s CLAUDE.md who its work is logged to — for example \u201cWork in this repository is logged to customer ACME, project PORT26\u201d. Then the agent does not have to ask every time, and suggestions do not land on the wrong customer.',

  // --- Yrityksen tunnus ----------------------------------------------------
  'tunnus.otsikko': 'Company logo',
  'tunnus.selite':
    'The logo appears in the top bar and on the sign-in page. Without one of your own, Devikone Oy’s logo is used.',
  'tunnus.koskeeKaikkia':
    'Unlike the theme and the language, the logo and the name are stored on the server and are visible to everyone.',
  'tunnus.nimi': 'Company name',
  'tunnus.nimiSelite':
    'Shown under the logo on the sign-in page, and used as the logo’s alternative text for screen readers.',
  'tunnus.tallennaNimi': 'Save name',
  'tunnus.tallennettu': 'Saved.',
  'tunnus.vaaleaTausta': 'For a light background',
  'tunnus.tummaTausta': 'For a dark background',
  'tunnus.oletus': 'Default logo',
  'tunnus.oletusKaytossa':
    'Devikone Oy’s logo, shipped with the application, is in use. Add your own logo to replace it.',
  'tunnus.rajat': (koko: string) =>
    `PNG, JPEG, WebP or SVG, at most ${koko}. If only one version is given, it is used on both backgrounds.`,
  'tunnus.lisaa': 'Choose an image',
  'tunnus.vaihda': 'Replace image',
  'tunnus.palautaOletus': 'Remove',
  'tunnus.esikatseluAlt': 'Logo preview',
  'tunnus.logoAlt': 'Company logo',
  'tunnus.virheTyyppi': 'The logo must be a PNG, JPEG, WebP or SVG image.',
  'tunnus.virheKoko': (koko: string) => `The image is too large. The maximum size is ${koko}.`,

  // --- Koulutukset (docs/KOULUTUKSET.md) ----------------------------------
  'navi.koulutukset': 'Training',

  'koulutus.otsikko': 'Training',
  'koulutus.johdanto':
    'These are the courses available to you. Mandatory ones must be completed, and a completion stays valid for a fixed period.',
  'koulutus.eiKoulutuksia': 'No courses are available to you at the moment.',
  'koulutus.pakollinen': 'Mandatory',
  'koulutus.vapaaehtoinen': 'Optional',
  'koulutus.kesto': (minuutit: number) => `about ${minuutit} min`,
  'koulutus.suoritettu': (pvm: string) => `Completed ${pvm}`,
  'koulutus.voimassa': (pvm: string) => `Valid until ${pvm}`,
  'koulutus.eiVanhene': 'Does not expire',
  'koulutus.vanheneePaivissa': (paivia: number) =>
    paivia === 0
      ? 'Expires today'
      : paivia === 1
        ? 'Expires tomorrow'
        : `Expires in ${paivia} days`,
  'koulutus.vanhentunut': (pvm: string) => `Expired ${pvm}`,
  'koulutus.eteneminen': (luettu: number, yhteensa: number) =>
    `${luettu}/${yhteensa} sections read`,

  'koulutus.tila.NOT_STARTED': 'Not started',
  'koulutus.tila.IN_PROGRESS': 'In progress',
  'koulutus.tila.EXAM_OPEN': 'Exam in progress',
  'koulutus.tila.PASSED': 'Valid',
  'koulutus.tila.EXPIRING': 'Expiring',
  'koulutus.tila.EXPIRED': 'Expired',

  'koulutus.varoitusPuuttuu': (maara: number) =>
    maara === 1
      ? 'You have a mandatory course outstanding.'
      : `You have ${maara} mandatory courses outstanding.`,
  'koulutus.varoitusVanhenee': (maara: number) =>
    maara === 1
      ? 'One of your completions is expiring.'
      : `${maara} of your completions are expiring.`,
  'koulutus.varoitusLinkki': 'View training',

  'koulutus.materiaali': 'Material',
  'koulutus.osio': 'Section',
  'koulutus.luettu': 'Read',
  'koulutus.aloitaMateriaali': 'Start the material',
  'koulutus.jatkaMateriaalia': 'Continue the material',
  'koulutus.kertaaMateriaali': 'Review the material',
  'koulutus.merkitseLuetuksi': 'Mark as read and continue',
  'koulutus.merkitseViimeinen': 'Mark as read and go back',
  'koulutus.edellinen': 'Previous',
  'koulutus.seuraava': 'Next',
  'koulutus.takaisinKoulutukseen': 'Back to the course',
  'koulutus.sisaltoaEiLoytynyt':
    'The content for this section was not found in the application. Please tell an administrator.',

  'koulutus.tentti': 'Exam',
  'koulutus.tenttiSaannot': (kysymyksia: number, oikein: number) =>
    `${kysymyksia} multiple-choice questions; ${oikein} correct answers pass.`,
  'koulutus.aikaraja': (minuutit: number) => `${minuutit} minutes to complete.`,
  'koulutus.eiAikarajaa': 'No time limit.',
  'koulutus.yhdellaIstumalla': (minuutit: number) =>
    `The exam is taken in one sitting: once started it closes after ${minuutit} minutes and cannot be resumed later.`,
  'koulutus.aloitaTentti': 'Start the exam',
  'koulutus.jatkaTenttia': 'Resume the exam',
  'koulutus.uusiTentti': 'Retake the exam',

  'koulutus.esto.MATERIAL_INCOMPLETE': 'Read the material before taking the exam.',
  'koulutus.esto.COOLDOWN': (kello: string) => `You can retake the exam at ${kello}.`,
  'koulutus.esto.ALREADY_PASSED': 'The course is completed and valid.',
  'koulutus.esto.ATTEMPT_OPEN': 'You have an exam in progress.',
  'koulutus.esto.NOT_PUBLISHED': 'The course is not available.',
  'koulutus.esto.PREREQUISITE_INCOMPLETE': (koulutus: string) =>
    `Complete the training "${koulutus}" first.`,

  'koulutus.esivaatimus': (koulutus: string) => `Requires the training ${koulutus}`,
  'koulutus.esivaatimusKunnossa': (koulutus: string) =>
    `The prerequisite ${koulutus} is completed and valid.`,
  'koulutus.esivaatimusPuuttuu': (koulutus: string) =>
    `The exam opens once the training "${koulutus}" is completed and valid. The material can be read already.`,
  'koulutus.siirryEsivaatimukseen': 'Go to the prerequisite',

  'koulutus.yritykset': 'Exam attempts',
  'koulutus.eiYrityksia': 'No exam attempts.',
  'koulutus.yritysTulos': (prosentti: number) => `${prosentti}%`,
  'koulutus.aikaLoppui': 'timed out',
  'koulutus.katsoTulos': 'View result',

  'koulutus.aani.otsikko': 'Listen to this section',
  'koulutus.aani.toista': 'Play',
  'koulutus.aani.tauko': 'Pause',
  'koulutus.aani.taakse': 'Skip back 15 seconds',
  'koulutus.aani.kelaus': 'Seek',
  'koulutus.aani.ilmoitus': 'The audio is AI-generated. The written material is the original.',
  'koulutus.aani.virhe': 'The audio could not be played. The material is available as text.',

  'tentti.kysymys': (nro: number, yhteensa: number) => `Question ${nro}/${yhteensa}`,
  'tentti.valitseKaikki':
    'This question may have more than one correct option. Select all of them — the question counts as correct only if every correct option is selected and no incorrect one is.',
  'tentti.vastattu': (vastattu: number, yhteensa: number) => `Answered ${vastattu}/${yhteensa}`,
  'tentti.aikaaJaljella': 'Time remaining',
  'tentti.aikaLoppumassa': 'Time is running out.',
  'tentti.palauta': 'Submit the exam',
  'tentti.palautetaan': 'Submitting…',
  'tentti.vahvistaOtsikko': 'Submit the exam?',
  'tentti.vahvistaTeksti': (vastaamatta: number) =>
    vastaamatta === 0
      ? 'All questions have been answered. Answers cannot be changed after submitting.'
      : `${vastaamatta} questions are unanswered and will count as wrong. Answers cannot be changed after submitting.`,
  'tentti.keskeytysVaroitus':
    'The exam is in progress. If you leave, the clock keeps running — come back before time runs out.',

  'tentti.tulosLapaisi': 'Exam passed',
  'tentti.tulosHylatty': 'Exam failed',
  'tentti.pisteet': (oikein: number, yhteensa: number, prosentti: number) =>
    `${oikein}/${yhteensa} correct (${prosentti}%)`,
  'tentti.rima': (oikein: number) => `${oikein} correct answers were required to pass.`,
  'tentti.aikaLoppuiSelite':
    'Time ran out. The answers given before the deadline were graded, and unanswered questions counted as wrong.',
  'tentti.suoritusVoimassa': (pvm: string) => `The completion is valid until ${pvm}.`,
  'tentti.suoritusEiVanhene': 'The completion does not expire.',
  'tentti.uusintaMilloin': (kello: string) => `You can retake the exam from ${kello}.`,
  'tentti.uusintaNyt': 'You can retake the exam right away.',
  'tentti.kayLapi': 'Answers and explanations',
  'tentti.sinunVastauksesi': 'Your answer',
  'tentti.oikeaVastaus': 'Correct answer',
  'tentti.eiVastausta': 'No answer',
  'tentti.perustelu': 'Explanation',

  // --- Koulutusten hallinta -----------------------------------------------
  'koulutusHallinta.otsikko': 'Training',
  'koulutusHallinta.johdanto':
    'Content and questions come from the code. Here you decide who a course applies to, how strict the exam is and how long a completion stays valid.',
  'koulutusHallinta.synkronoi': 'Pull content from code',
  'koulutusHallinta.synkronoidaan': 'Pulling…',
  'koulutusHallinta.synkronoitu': (muutoksia: number) =>
    muutoksia === 0 ? 'The content was already up to date.' : `${muutoksia} courses updated.`,
  'koulutusHallinta.versio': (versio: number) => `Content version ${versio}`,
  'koulutusHallinta.paivitetty': (aika: string) => `Pulled from code ${aika}`,
  'koulutusHallinta.eiHaettu': 'Content has not been pulled from the code yet.',
  'koulutusHallinta.osioita': (maara: number) => `${maara} sections`,
  'koulutusHallinta.kysymyspankki': (maara: number) => `${maara} questions in the bank`,
  'koulutusHallinta.tilanne': (voimassa: number, kohdennettu: number) =>
    `${voimassa}/${kohdennettu} valid`,
  'koulutusHallinta.puuttuu': (maara: number) => `${maara} outstanding`,
  'koulutusHallinta.tilanneAvaa': 'Show who has completed it',
  'koulutusHallinta.puuttuuAvaa': 'Show who is missing it',

  'koulutusHallinta.kohdejoukko': (koulutus: string) => `Audience — ${koulutus}`,
  'koulutusHallinta.kohdejoukkoSelite':
    'The people this training is mandatory for, plus those who completed it without being required to.',
  'koulutusHallinta.suodatin.KAIKKI': 'All',
  'koulutusHallinta.suodatin.VOIMASSA': 'Valid',
  'koulutusHallinta.suodatin.PUUTTUU': 'Outstanding',
  'koulutusHallinta.suodatin.VAPAAEHTOINEN': 'Voluntary',
  'koulutusHallinta.eiKetaan': 'Nobody matches this filter.',
  'koulutusHallinta.eiKohdejoukkoa':
    'The training is not assigned to anyone. An unpublished training is on nobody’s list even if it has been assigned.',
  'koulutusHallinta.suoritettu': (paiva: string) => `Completed ${paiva}`,
  'koulutusHallinta.eiSuoritusta': 'Not completed',
  'koulutusHallinta.yrityksia': (maara: number) =>
    maara === 1 ? '1 exam attempt' : `${maara} exam attempts`,

  'koulutusHallinta.asetukset': 'Exam rules and validity',
  'koulutusHallinta.tila': 'Publication status',
  'koulutusHallinta.tila.DRAFT': 'Draft',
  'koulutusHallinta.tila.PUBLISHED': 'Published',
  'koulutusHallinta.tila.ARCHIVED': 'Archived',
  'koulutusHallinta.lapaisyraja': 'Pass mark (%)',
  'koulutusHallinta.kysymyksia': 'Questions per exam',
  'koulutusHallinta.aikaraja': 'Time limit (min)',
  'koulutusHallinta.aikarajaSelite': 'Empty = no time limit.',
  'koulutusHallinta.sessio': 'Session limit (min)',
  'koulutusHallinta.sessioSelite':
    'How long a started exam stays open. This is the one-sitting rule, not a time limit.',
  'koulutusHallinta.jaahy': 'Cooldown after a failure (min)',
  'koulutusHallinta.voimassaolo': 'Validity (months)',
  'koulutusHallinta.voimassaoloSelite': 'Empty = the completion does not expire.',
  'koulutusHallinta.varoitus': 'Warn before expiry (days)',
  'koulutusHallinta.materiaaliPakko': 'The material must be read before the exam',
  'koulutusHallinta.esivaatimus': 'Prerequisite',
  'koulutusHallinta.esivaatimusSelite':
    'This training must be completed and valid before the exam can be started. The prerequisite does not block reading the material.',
  'koulutusHallinta.eiEsivaatimusta': 'No prerequisite',
  'koulutusHallinta.esivaatimusRivi': (koulutus: string) => `Prerequisite: ${koulutus}`,

  'koulutusHallinta.kohdennukset': 'Assignments',
  'koulutusHallinta.kohdennusSelite':
    'A role assigns the course to everyone in it, including new users. A personal assignment overrides the role.',
  'koulutusHallinta.roolit': 'Roles',
  'koulutusHallinta.henkilot': 'Individual people',
  'koulutusHallinta.eiKohdennettu': 'Not assigned',
  'koulutusHallinta.pakollinen': 'Mandatory',
  'koulutusHallinta.tarjolla': 'Optional',
  'koulutusHallinta.lisaaHenkilo': 'Add a person',
  'koulutusHallinta.poista': 'Remove assignment',

  'koulutusHallinta.kirjaaSuoritus': 'Record a completion manually',
  'koulutusHallinta.kirjaaSelite':
    'The course may have been taken elsewhere. A manually recorded completion names who recorded it and why.',
  'koulutusHallinta.henkilo': 'Person',
  'koulutusHallinta.suorituspaiva': 'Completion date',
  'koulutusHallinta.peruste': 'What the completion is based on',
  'koulutusHallinta.kirjaa': 'Record completion',
  'koulutusHallinta.kirjattu': 'Completion recorded.',

  // --- Koulutusraportti ----------------------------------------------------
  'koulutusRaportti.otsikko': 'Training status',
  'koulutusRaportti.johdanto':
    'Training status per course across the organisation. This is the view whose contents get asked for in customer contracts and tenders.',
  'koulutusRaportti.kaikkiKoulutukset': 'All courses',
  'koulutusRaportti.vainOngelmat': 'Show only people with gaps',
  'koulutusRaportti.myosPassivoidut': 'Include deactivated users',
  'koulutusRaportti.eiRiveja': 'No rows to show.',
  'koulutusRaportti.henkilo': 'Person',
  'koulutusRaportti.kunnossa': 'All mandatory courses valid',
  'koulutusRaportti.puutteita': 'Gaps',
  'koulutusRaportti.vanhenemassa': 'Expiring',
  'koulutusRaportti.kayttajia': 'Users',
  'koulutusRaportti.laadittu': (aika: string) => `Status as of ${aika}`,
  'koulutusRaportti.eiKohdennettu': 'Not assigned',

  // --- Attributes ---------------------------------------------------------
  'attribuutit.otsikko': 'Attributes',
  'attribuutit.johdanto':
    'Extra fields for customers and projects — the customer\u2019s own project code, for example. The key is defined once here; the value is entered on the customer or project.',
  'attribuutit.uusi': 'New attribute',
  'attribuutit.muokkaaOtsikko': (nimi: string) => `Attribute · ${nimi}`,
  'attribuutit.uusiOtsikko': 'New attribute',
  'attribuutit.avain': 'Key',
  'attribuutit.avainSelite':
    'Technical identifier: lower-case letters, digits and underscores. The key cannot be changed later — it is the CSV column name.',
  'attribuutit.avainLukittu': 'The key cannot be changed.',
  'attribuutit.otsikkoKentta': 'Label',
  'attribuutit.otsikkoSelite': 'The name shown on forms and reports.',
  'attribuutit.kohde': 'Applies to',
  'attribuutit.kohdeCUSTOMER': 'Customer',
  'attribuutit.kohdePROJECT': 'Project',
  'attribuutit.kohdeBOTH': 'Customer and project',
  'attribuutit.kohdeSelite':
    'Customer and project: the value is given on the customer and inherited by its projects unless the project has its own.',
  'attribuutit.muoto': 'Format rule',
  'attribuutit.muotoSelite':
    'Optional regular expression the value must match. For example ^B\\d{9}$.',
  'attribuutit.pakollinen': 'Required',
  'attribuutit.pakollinenSelite': ' — enforced when creating a new customer or project',
  'attribuutit.naytaRaportilla': 'Show on reports',
  'attribuutit.jarjestys': 'Order',
  'attribuutit.kaytossa': 'In use',
  'attribuutit.poistettuKaytosta': 'disabled',
  'attribuutit.csvSarake': (avain: string) => `CSV column: attr_${avain}`,
  'attribuutit.arvoja': (maara: number) => (maara === 1 ? '1 value set' : `${maara} values set`),
  'attribuutit.eiArvoja': 'No values',
  'attribuutit.poistaVahvistus': (nimi: string) =>
    `Delete the attribute ${nimi}? This works only if it has no values.`,
  'attribuutit.eiAttribuutteja': 'No attributes defined yet. Create the first one above.',
  'attribuutit.lisatiedot': 'Additional details',
  'attribuutit.eiKohteelle': 'No attributes are defined for this target.',
  'attribuutit.peritty': 'inherited from customer',
  'attribuutit.perittyArvo': (arvo: string) => `Inherited from customer: ${arvo}`,

  // --- Export columns -----------------------------------------------------
  'vienti.sarakkeet': 'CSV columns',
  'vienti.sarakkeetSelite':
    'Choose what the file contains. Description and attributes are not included by default — the description is an internal note that rarely belongs on an invoice.',
  'vienti.sarakkeitaValittu': (valittu: number, kaikki: number) =>
    `${valittu}/${kaikki} columns selected`,
  'vienti.palautaOletukset': 'Restore defaults',
  'vienti.sarakeAttribuutit': 'Attributes',
  'vienti.eiSarakkeita': 'Select at least one column.',
  'vienti.vainCsv': 'The column choice applies to CSV only. JSON always contains every field.',
  'vienti.sarake.pvm': 'Date',
  'vienti.sarake.viikko': 'Week',
  'vienti.sarake.kayttaja': 'Person',
  'vienti.sarake.kayttaja_email': 'Person\u2019s email',
  'vienti.sarake.asiakas_koodi': 'Customer code',
  'vienti.sarake.asiakas_nimi': 'Customer name',
  'vienti.sarake.tehtavalaji_koodi': 'Task type code',
  'vienti.sarake.tehtavalaji_nimi': 'Task type name',
  'vienti.sarake.projekti_koodi': 'Project code',
  'vienti.sarake.projekti_nimi': 'Project name',
  'vienti.sarake.tunnit': 'Hours',
  'vienti.sarake.laskutettava': 'Billable',
  'vienti.sarake.tuntihinta': 'Hourly rate',
  'vienti.sarake.arvo': 'Value',
  'vienti.sarake.valuutta': 'Currency',
  'vienti.sarake.kuvaus': 'Description',
  'vienti.sarake.kirjaus_id': 'Entry id',
  'vienti.sarake.luotu': 'Created',
  'vienti.sarake.muokattu': 'Updated',
  // --- Taulut (docs/PROJEKTINHALLINTA.md) ---------------------------------
  'taulut.otsikko': 'Boards',
  'taulut.johdanto':
    'A kanban board for every customer and each of its projects. You see the customers you have been added to.',
  'taulut.eiTauluja':
    'You have not been added to any customer. Ask an administrator to add you as a member.',
  'taulut.hae': 'Find a board',
  'taulut.hakuohje': 'Customer or project code or name. Enter opens the only match.',
  'taulut.eiHakutuloksia': 'No board matches the search.',
  'taulut.viimeksiKaytetyt': 'Recently used',
  'taulut.asiakkuudenTaulu': 'Customer board',
  'taulut.tehtavia': (maara: number) => (maara === 1 ? '1 task' : `${maara} tasks`),
  'taulut.passivoitu': 'Inactive',
  'taulut.lukutila':
    'This board is read-only: the project is closed or the customer is inactive. The content is visible but cannot be edited.',
  'taulut.vainLuku': 'Read-only',
  'taulut.kaikkiTaulut': 'All boards',
  'taulut.eiLoytynyt': 'Board not found, or you do not have access to it.',
  'taulut.uusiUimarata': 'New swimlane',
  'taulut.uimaradanNimi': 'Name of the integration or app',
  'taulut.lisaa': 'Add',
  'taulut.eiUimaratoja':
    'This board has no swimlanes yet. Add the first one — the name of the integration or app is enough.',
  'taulut.uimaradanToiminnot': 'Swimlane actions',
  'taulut.nimeaUimarata': 'Rename swimlane',
  'taulut.siirraVasemmalle': 'Move swimlane left',
  'taulut.siirraOikealle': 'Move swimlane right',
  'taulut.poistaUimarata': 'Delete swimlane',
  'taulut.poistetaankoUimarata': (nimi: string, maara: number) =>
    `Delete swimlane “${nimi}” and its ${maara === 1 ? '1 task' : `${maara} tasks`}? This cannot be undone.`,
  'taulut.osio.INFO': 'INFO',
  'taulut.osio.TODO': 'TODO',
  'taulut.osio.DOING': 'DOING',
  'taulut.osio.BLOCKED': 'BLOCKED',
  'taulut.osio.IN_REVIEW': 'IN REVIEW',
  'taulut.osio.DONE': 'DONE',
  'taulut.osio.SKIPPED': 'SKIPPED',
  'taulut.tila.PLANNED': 'Planned',
  'taulut.tila.ACTIVE': 'In progress',
  'taulut.tila.DONE': 'Done',
  'taulut.tila': 'Status',
  'taulut.uimaradanTiedot': 'Swimlane details',
  'taulut.uimaradanTiedotSelite':
    'Estimate in person-days at half-day precision, start and planned completion, and status. Logged person-days come from entries linked to the tasks.',
  'taulut.arvioHtp': 'Estimate (person-days)',
  'taulut.aloitus': 'Start',
  'taulut.valmistuminen': 'Planned completion',
  'taulut.lisaaTiedot': 'Add details',
  'taulut.muokkaa': 'Edit',
  'taulut.eiTietoja': 'No schedule or estimate',
  'taulut.htp': 'pd',
  'taulut.htpSelite': 'Logged person-days / estimate',
  'taulut.myohassa': 'late',
  'taulut.raportti': 'Report',
  'taulut.raporttiSelite': 'Per-task breakdown of status history and lead time',
  'taulut.milestone': 'Milestone',
  'taulut.milestonet': 'Milestones',
  'taulut.muokkaaMilestoneja': 'Milestones',
  'taulut.milestonetSelite':
    'Milestones are checkpoints of the swimlane that tasks are attached to. They are ordered by due date, and the next unreached one is highlighted. You mark a milestone reached yourself — finishing its tasks does not do it automatically.',
  'taulut.lisaaMilestone': 'Add milestone',
  'taulut.poistaMilestone': 'Delete milestone',
  'taulut.poistetaankoMilestone': (nimi: string, maara: number) =>
    `Delete milestone “${nimi}”?${maara > 0 ? ` ${maara === 1 ? '1 task is detached' : `${maara} tasks are detached`} from it, but no tasks are deleted.` : ''}`,
  'taulut.milestoneNimi': 'Name',
  'taulut.erapaiva': 'Due date',
  'taulut.erapaivaPvm': (pvm: string) => `Due ${pvm}`,
  'taulut.eiErapaivaa': 'No due date',
  'taulut.milestoneKuvausVihje': 'What this milestone delivers (optional)',
  'taulut.milestoneEdistyminen': (valmiit: number, kaikki: number) =>
    `${valmiit}/${kaikki} tasks done`,
  'taulut.saavutettu': 'reached',
  'taulut.saavutettuPvm': (pvm: string) => `Reached ${pvm}`,
  'taulut.merkitseSaavutetuksi': 'Mark as reached',
  'taulut.palautaKeskeneraiseksi': 'Mark as not reached',
  'taulut.naytaSaavutetut': (maara: number) => `Reached (${maara})`,
  'taulut.piilotaSaavutetut': 'Hide reached',
  'taulut.suodataMilestonella': (nimi: string) => `Show only tasks of milestone “${nimi}”`,
  'taulut.naytaKaikkiTehtavat': 'Show all tasks',
  'taulut.milestoneKortilla': (nimi: string) => `Milestone: ${nimi}`,
  'taulut.eiMilestonea': 'No milestone',
  'taulut.eiMilestonejaRadalla': 'The swimlane has no milestones',
  'taulut.deadline': 'Deadline',
  'taulut.deadlinePvm': (pvm: string) => `Deadline ${pvm}`,
  'taulut.deadlineModeraattorille':
    'The deadline is set by an account manager or an administrator.',
  'taulut.deadlineMilestonenJalkeen': (pvm: string) =>
    `The deadline is after the milestone's due date (${pvm}).`,
  'taulut.deadlineAlkuperainen': (pvm: string, kertaa: number) =>
    `Originally ${pvm} · moved ${kertaa === 1 ? 'once' : `${kertaa} times`}`,
  'taulut.deadlineSiirretty': (mista: string, mihin: string) => `Deadline ${mista} → ${mihin}`,
  'taulut.deadlinePoistettu': (pvm: string) => `Deadline removed (was ${pvm})`,
  'taulut.siirronSyy': 'Reason for the change',
  'taulut.siirronSyyEiKirjattu': 'Not recorded',
  'taulut.siirronSyySelite':
    'Whose side is the deadline moving for? Delivery is compared with the original date.',
  'taulut.siirronSyy.OWN': 'Our side',
  'taulut.siirronSyy.CUSTOMER': 'Customer',
  'taulut.siirronSyy.PARTNER': 'Partner',
  'taulut.vastuuMuutos': (nimi: string) => `Assignee: ${nimi}`,
  'taulut.vainMyohassa': 'Overdue',
  'taulut.vainMyohassaSelite': 'Show only open tasks whose deadline has passed',
  'taulut.milestoneIrtoaa':
    'Moving to another swimlane detaches the task from its current milestone.',
  'taulut.milestoneKirjattu': (tunnit: string) => `${tunnit} logged`,
  'taulut.milestoneKirjattuSelite':
    'Hours logged on tasks attached to this milestone, within what you can see.',
  'taulut.seuraavaMilestone': (nimi: string, pvm: string) => `Next milestone: ${nimi} (${pvm})`,
  'taulut.seuraavaMilestoneLyhyt': 'Next milestone',
  'taulut.ganttMilestone.kesken': 'Milestone',
  'taulut.ganttMilestone.saavutettu': 'Reached milestone',
  'taulut.ganttMilestone.myohassa': 'Overdue milestone',
  'taulut.ganttMilestoneVihje': (nimi: string, pvm: string) => `${nimi}, due ${pvm}`,
  'taulut.rajaaTehtavia': 'Filter tasks',
  'taulut.haeTehtavista': 'Search tasks',
  'taulut.kaikkiVastuuhenkilot': 'All assignees',
  'taulut.vainOmat': 'Only mine',
  'taulut.rajausTulos': (nakyvia: number, kaikki: number) => `Showing ${nakyvia}/${kaikki} tasks`,
  'taulut.tyhjennaRajaus': 'Clear filter',
  'taulut.taulu': 'Board',
  'taulut.tamaTaulu': 'This board',
  'taulut.siirtoTaululleVihje':
    'The task moves to another board of the same customer. Its milestone is detached; comments, relations and time entries stay.',
  'taulut.kohdetaulullaEiUimaratoja': 'The target board has no swimlanes.',
  'taulut.siirrettyTaululle': (taulu: string) => `Task moved to ${taulu}.`,
  'taulut.asetusMilestone': 'Reminder of a milestone due date',
  'taulut.asetusMilestoneSelite':
    'To the milestone creator and the account managers three days before an unreached milestone is due, and weekly while it is overdue.',
  'asiakasraportti.avaa': 'Customer report',
  'asiakasraportti.avaaSelite': 'Status report for the customer as Markdown',
  'asiakasraportti.otsikko': 'Customer report',
  'asiakasraportti.selite':
    'A status report to send to the customer: swimlanes, milestones, unfinished and waiting tasks of the next milestone, and tasks completed during the period. Descriptions, comments, assignees and INFO tasks are left out.',
  'asiakasraportti.laajuus': 'Scope',
  'asiakasraportti.tamaTaulu': 'This board',
  'asiakasraportti.kaikkiTaulut': (asiakas: string) => `All boards of ${asiakas}`,
  'asiakasraportti.valmistuneet': 'Completed',
  'asiakasraportti.paivaa': (paivia: number) => `${paivia} days`,
  'asiakasraportti.kieli': 'Language',
  'asiakasraportti.suomi': 'Suomi',
  'asiakasraportti.englanti': 'English',
  'asiakasraportti.tunnit': 'Show logged hours',
  'asiakasraportti.tunnitSelite':
    'Hours logged on swimlanes and milestones. Choose this only if the contract calls for reporting them to the customer.',
  'asiakasraportti.nakyma': 'View',
  'asiakasraportti.esikatselu': 'Preview',
  'asiakasraportti.markdownTeksti': 'Report as Markdown text',
  'asiakasraportti.tarkista': 'Read the report before sending: task titles appear as they are.',
  'asiakasraportti.kopioi': 'Copy',
  'asiakasraportti.kopioitu': 'Copied',
  'asiakasraportti.lataa': 'Download .md',
  'taulut.takaisinTaululle': 'Back to the board',
  'taulut.lataaCsv': 'Download CSV',
  'taulut.aikataulu': 'Schedule',
  'taulut.lapimenoaika': 'Lead time',
  'taulut.alkaen': (alku: string) => `from ${alku}`,
  'taulut.eiValmis': 'not done yet',
  'taulut.jumissaNyt': 'Blocked now',
  'taulut.vastuuhenkilo': 'Assignee',
  'taulut.lisatty': 'Added',
  'taulut.paattynyt': 'Resolved',
  'taulut.siirtymia': 'Transitions',
  'taulut.eiTehtavia': 'The swimlane has no tasks.',
  'taulut.raporttiHuomio': (aika: string) =>
    `Durations are calendar time, not working days; the open column is counted up to ${aika}.`,
  'taulut.tilahistoria': 'Status history',
  'taulut.tapahtumat': (maara: number) => `${maara} events`,
  'taulut.kestoMinuutit': (min: number) => `${min} min`,
  'taulut.kestoTunnit': (h: number) => `${h} h`,
  'taulut.kestoPaivat': (pv: number) => `${pv} d`,
  'taulut.ruuhka': (maara: number) =>
    `${maara} tasks in DOING for the same assignee \u2014 more than three at once rarely get finished.`,
  'taulut.ruuhkaTaululla': 'More than three tasks in progress on this board',
  'taulut.omatTehtavat': 'My tasks',
  'taulut.gantt': 'Gantt',
  'taulut.ganttSelite':
    'Every swimlane on a timeline by start and planned completion. The purple strip at the bottom of a bar is the longest-blocked task\u2019s time; the number at the end is the count of blocked tasks.',
  'taulut.naytaValmiit': 'Show done as well',
  'taulut.selite': 'Legend',
  'taulut.ganttJumissaSelite': 'Blocked (longest)',
  'taulut.eiAjoitettuja': 'No swimlane has both a start and a completion date.',
  'taulut.ilmanAikataulua': 'Without a schedule',
  'taulut.vk': (n: number) => `wk ${n}`,
  'taulut.jumissaMaara': (n: number) => (n === 1 ? '1 task blocked' : `${n} tasks blocked`),
  'taulut.tehtaviaValmiina': (valmis: number, kaikki: number) =>
    `${valmis}/${kaikki} tasks resolved`,
  'taulut.ganttJumissaVihje': (otsikko: string, pv: number) =>
    `Blocked longest: ${otsikko}, ${pv} d`,
  'taulut.ganttHuomio': (pvm: string) =>
    `The vertical line is today (${pvm}). The swimlane name opens the report, the customer name the board.`,
  'taulut.vastuuAsiakas': 'Customer',
  'taulut.vastuuKumppani': 'Partner',
  'taulut.asiakkaanHenkilo': (nimi: string) => `${nimi} (customer)`,
  'taulut.asiakasTasolla': 'Customer, no specific person',
  'taulut.odottaaUlkoista': (nimi: string) => `Waiting on ${nimi}`,
  'taulut.ulkoinenVastuuSelite':
    'The task is waiting on the customer or a partner. They get no notifications, and the agent does not move the task.',
  'kumppanit.otsikko': 'Partners',
  'kumppanit.selite':
    'Suppliers and integration partners a task can be assigned to. The register is shared by all customers.',
  'kumppanit.uusi': 'New partner',
  'kumppanit.muokkaa': 'Edit partner',
  'kumppanit.kuvaus': 'Description',
  'kumppanit.kuvausVihje': 'What the partner does and with which customers.',
  'kumppanit.eiKumppaneita': 'There are no partners in the register.',
  'kumppanit.passivoitu': 'inactive',
  'kumppanit.passivoi': 'Deactivate',
  'kumppanit.aktivoi': 'Activate',
  'kumppanit.avoimiaTehtavia': (maara: number) =>
    maara === 1 ? '1 open task' : `${maara} open tasks`,
  'kumppanit.poistaVahvistus': (nimi: string) => `Delete partner ${nimi}?`,
  'kumppanit.naytaPassivoidut': (maara: number) => `Show inactive (${maara})`,
  'jumissa.otsikko': 'Blocked',
  'jumissa.selite': (raja: number) =>
    `Every task in the BLOCKED section across your boards, longest-blocked first. A red number means more than ${raja} days \u2014 the same limit triggers the email reminder. Boards of closed projects and deactivated customers are not shown.`,
  'jumissa.eiJumissa': 'No task is blocked on your boards.',
  'jumissa.rajaus': 'Filter',
  'jumissa.kaikki': 'Blocked in total',
  'jumissa.kaikkiVihje': 'Show all blocked tasks',
  'jumissa.yliRajan': (raja: number) => `Over ${raja} days`,
  'jumissa.yliRajanVihje': 'Blocked long enough that the reminder has already gone out',
  'jumissa.hiljaa': 'No explanation',
  'jumissa.hiljaaVihje': 'Nobody has commented on the task since it was blocked',
  'jumissa.odottaa': 'Waiting for another task',
  'jumissa.odottaaVihje': 'The task depends on an unfinished task',
  'jumissa.asiakkaittain': 'By customer',
  'jumissa.kaikkiAsiakkaat': 'All customers',
  'jumissa.haku': 'Search task, swimlane, customer or person',
  'jumissa.jarjestys': 'Sort',
  'jumissa.jarjestysJumissa': 'Blocked longest',
  'jumissa.jarjestysHiljaa': 'Quiet longest',
  'jumissa.jarjestysAsiakas': 'By customer',
  'jumissa.jarjestysMilestone': 'By next milestone',
  'jumissa.milestone': 'Holds up a milestone',
  'jumissa.milestoneVihje': "The task belongs to the swimlane's next unreached milestone",
  'jumissa.jarruttaaMilestonea': 'next milestone',
  'jumissa.kopioi': 'Copy list',
  'jumissa.kopioiVihje': 'Copy the visible tasks as text for a meeting or a message',
  'jumissa.kopioitu': 'Copied',
  'jumissa.kopioRivi': (paivia: number, vastuu: string) =>
    `blocked ${paivia} d, assignee: ${vastuu}`,
  'jumissa.kopioViimeisin': (nimi: string, aika: string) => `Latest comment ${nimi} ${aika}`,
  'jumissa.eiOsumia': 'No task matches the filter.',
  'jumissa.nakyvissa': (nakyvat: number, kaikki: number) =>
    `${nakyvat}/${kaikki} blocked tasks shown.`,
  'jumissa.nakyvissaAsiakas': (nakyvat: number, asiakas: string) =>
    `${nakyvat} tasks shown for ${asiakas}.`,
  'jumissa.pvJumissa': 'days blocked',
  'jumissa.jumissaAlkaen': (aika: string) => `Blocked since ${aika}`,
  'jumissa.siirsi': (nimi: string, aika: string) => `Blocked by ${nimi} ${aika}`,
  'jumissa.kerta': (kerta: number) =>
    `Blocked for the ${kerta === 2 ? '2nd' : kerta === 3 ? '3rd' : `${kerta}th`} time`,
  'jumissa.toistuvaVihje':
    'The task has been in BLOCKED before \u2014 a recurring block may point to a structural obstacle.',
  'jumissa.odottaaTehtavaa': 'Waiting for:',
  'jumissa.eiKommenttiaJalkeen':
    'No comment since it was blocked \u2014 the reason is not on the board.',
  'jumissa.ennenJumiutumista': 'before it was blocked',
  'jumissa.tanaan': 'today',
  'jumissa.eilen': 'yesterday',
  'jumissa.paivaaSitten': (paivia: number) => `${paivia} d ago`,
  'jumissa.ganttLinkki': 'Show blocked tasks',
  'taulut.omatSelite':
    'Open tasks assigned to you across all your boards, in-progress first. Click to open the task on its board.',
  'taulut.eiOmiaTehtavia': 'No open tasks are assigned to you.',
  'taulut.aiTehtavat': 'Assigned to the AI assistant',
  'taulut.aiTehtavatSelite':
    'Tasks meant for the agent to pick up on your boards. Your agent sees the same list via the my_tasks tool.',
  'taulut.eiAiTehtavia': 'No open tasks are assigned to the AI assistant.',
  'taulut.naytaKaikki': (maara: number) => `Show all (${maara})`,
  'taulut.naytaVahemman': 'Show fewer',
  'taulut.ohitaTehtava': 'Will not be done',
  'taulut.ohitaSelite':
    'The task moves to SKIPPED. Write why it will not be done \u2014 the reason is saved as a comment on the task.',
  'taulut.ohitusPerustelu': 'Why not',
  'taulut.ohitusPerusteluVihje': 'e.g. analysis showed the current implementation is enough',
  'taulut.ohitusPerusteluPuuttuu': 'Moving to SKIPPED requires a reason.',
  'taulut.ohita': 'Move to SKIPPED',
  'taulut.ohitettu': 'Task marked as not to be done.',
  'taulut.lisaaTehtava': (osio: string) => `Add a task to ${osio}`,
  'taulut.tehtavanOtsikko': 'Task title',
  'taulut.avaaTehtava': 'Open task',
  'taulut.tehtava': 'Task',
  'taulut.monistaTehtava': 'Duplicate the task in the same section',
  'taulut.poistaTehtava': 'Delete task',
  'taulut.poistetaankoTehtava': 'Delete this task? This cannot be undone.',
  'taulut.otsikkoKentta': 'Title',
  'taulut.kuvaus': 'Description',
  'taulut.tyomaara': 'Estimate',
  'taulut.eiArviota': 'No estimate',
  'taulut.eiVastuuhenkiloa': 'Unassigned',
  'taulut.aiAssistentti': 'AI assistant',
  'taulut.kopioiLinkki': 'Copy link',
  'taulut.kirjaaTunteja': 'Log hours',
  'taulut.kirjaaTuntejaSelite':
    'Opens the calendar for today with the form prefilled with the customer, project and title.',
  'taulut.kirjattu': (tunnit: string, maara: number) =>
    `${tunnit} logged (${maara === 1 ? '1 entry' : `${maara} entries`})`,
  'taulut.kirjattuSelite': 'Entries copied from this task within your visibility.',
  'taulut.relaatiot': 'Relations',
  'taulut.lisaaRelaatio': 'Add relation',
  'taulut.poistaRelaatio': 'Remove relation',
  'taulut.eiRelaatioita': 'No relations to other tasks.',
  'taulut.relaationLaji': 'Relation type',
  'taulut.haeTehtavaa': 'Search tasks by title\u2026',
  'taulut.eiTehtavaOsumia': 'No matches on this customer\u2019s boards.',
  'taulut.relaatioita': (maara: number) => (maara === 1 ? '1 relation' : `${maara} relations`),
  'taulut.odottaaTehtavia': (maara: number) =>
    maara === 1 ? 'Waiting on one unfinished task' : `Waiting on ${maara} unfinished tasks`,
  'taulut.relaatio.DEPENDS_ON.OUT': 'Depends on',
  'taulut.relaatio.DEPENDS_ON.IN': 'Blocks',
  'taulut.relaatio.DUPLICATE_OF.OUT': 'Duplicate of',
  'taulut.relaatio.DUPLICATE_OF.IN': 'Duplicated by',
  'taulut.relaatio.RELATES_TO.OUT': 'Relates to',
  'taulut.relaatio.RELATES_TO.IN': 'Relates to',
  'taulut.relaatio.FOLLOWS.OUT': 'Follows',
  'taulut.relaatio.FOLLOWS.IN': 'Precedes',

  // --- Reminders (chapter 24) ---------------------------------------------
  'taulut.muistutukset': 'Reminders',
  'taulut.lisaaMuistutus': 'Set a reminder',
  'taulut.asetaMuistutus': 'Set',
  'taulut.eiMuistutuksia': 'No reminders.',
  'taulut.muistutusSelite':
    'An email at the chosen time. Only an account manager or an admin can target the assignee or both.',
  'taulut.peruMuistutus': 'Cancel reminder',
  'taulut.muistutusPaiva': 'Date',
  'taulut.muistutusKellonaika': 'Time',
  'taulut.muistutusKenelle': 'Recipients',
  'taulut.muistutusSaate': 'Note',
  'taulut.muistutusSaateVihje': 'What is this about?',
  'taulut.muistutusKohde.SETTER': 'Me',
  'taulut.muistutusKohde.ASSIGNEE': 'Assignee',
  'taulut.muistutusKohde.BOTH': 'Both',
  'taulut.muistutusEsivalinta.TOMORROW': 'Tomorrow',
  'taulut.muistutusEsivalinta.NEXT_MONDAY': 'Next Monday',
  'taulut.muistutusEsivalinta.WEEKS_1': '1 wk',
  'taulut.muistutusEsivalinta.WEEKS_2': '2 wks',
  'taulut.muistutusEsivalinta.WEEKS_4': '4 wks',
  'taulut.muistutusEnnenDeadlinea': 'Before deadline',
  'taulut.muistutusEnnenDeadlineaSelite': (pvm: string) =>
    `On the working day before the deadline, ${pvm}`,
  'taulut.muistutusOmaAika': 'Custom time',
  'taulut.muistutusLahetetty': 'sent',
  'taulut.muistutusAsetti': (nimi: string) => `set by ${nimi}`,
  'taulut.muistutusKortilla': (aika: string) => `Reminder ${aika}`,
  'taulut.muistutusAsetettu': 'Reminder set.',
  'taulut.omatMuistutukset': 'Reminders',
  'taulut.omatMuistutuksetSelite':
    'Pending reminders that concern you. The email goes out at the chosen time.',
  'taulut.linkkiKopioitu': 'Link copied',
  'taulut.asetukset': 'Notifications',
  'taulut.asetuksetSelite':
    'Email notifications for this board. The setting applies to every member of the board and can be changed by the account manager or an admin.',
  'taulut.asetuksetTallennettu': 'Notification settings saved.',
  'taulut.asetusJumissa': 'Weekly reminder about blocked tasks',
  'taulut.asetusJumissaSelite':
    'To the creator and the account manager once a task has sat in BLOCKED for more than eight days. Requires the reminder to be enabled under Admin \u2192 Settings.',
  'taulut.asetusBlocked': 'Message as soon as a task moves to BLOCKED',
  'taulut.asetusBlockedSelite':
    'To the creator and the account manager, not to the person who moved it.',
  'taulut.asetusOsoitettu': 'Message when a task is assigned to someone',
  'taulut.asetusOsoitettuSelite': 'To the assignee, unless they assigned the task to themselves.',
  'taulut.asetusMaininta': 'Email when someone is mentioned in a description or comment',
  'taulut.asetusMainintaSelite':
    'To the mentioned member (@handle), not to the author. The mention shows up in My tasks regardless.',
  'taulut.maininnat': 'Mentions',
  'taulut.mainintaSelite':
    'You were mentioned by @handle in these tasks. Dismissing removes the row from the list.',
  'taulut.eiMainintoja': 'No unread mentions.',
  'taulut.mainitsiKommentissa': (nimi: string) => `${nimi} mentioned you in a comment`,
  'taulut.mainitsiKuvauksessa': (nimi: string) => `${nimi} mentioned you in the description`,
  'taulut.kuittaa': 'Dismiss',
  'taulut.kuittaaKaikki': 'Dismiss all',
  'taulut.mainintaMaara': (maara: number) =>
    maara === 1 ? '1 unread mention' : `${maara} unread mentions`,
  'taulut.mainintaVihje': 'Type @ and the start of a name to mention a member.',
  'taulut.esikatsele': 'Preview',
  'taulut.muokkaaKuvausta': 'Edit description',
  'taulut.eiKuvausta': 'No description.',
  'taulut.muotoiluVihje': 'Markdown is supported, and a ```mermaid block renders as a diagram.',
  'taulut.mainintaEhdotukset': 'Members',
  'taulut.reagoi': 'React',
  'taulut.reaktio.THUMBS_UP': 'Agree',
  'taulut.reaktio.CHECK': 'Done',
  'taulut.reaktio.EYES': 'Seen',
  'taulut.reaktio.RED_FLAG': 'Critical',
  'taulut.reaktionTiedot': (merkitys: string, nimet: string) => `${merkitys}: ${nimet}`,
  'taulut.tehtavaAgentilta': (lahde: string) =>
    `Created by an agent on the creator\u2019s behalf (${lahde})`,
  'taulut.uimarata': 'Swimlane',
  'taulut.osio': 'Section',
  'taulut.jumissa': (paivaa: number) => `Blocked for ${paivaa} days`,
  'taulut.luonut': (nimi: string) => `Created by ${nimi}`,
  'taulut.eiMuokkausoikeutta':
    'You can move this task, but only its creator, the account manager or an administrator can edit its content.',
  'taulut.kommentit': 'Comments',
  'taulut.kommentitSelite':
    'What has been done or noticed on this task. Time goes to the timesheet, not here.',
  'taulut.eiKommentteja': 'No comments yet.',
  'taulut.kommentteja': (maara: number) => (maara === 1 ? '1 comment' : `${maara} comments`),
  'taulut.uusiKommentti': 'New comment',
  'taulut.kommentinVihje': 'What did you do or notice? @ mentions a member, Ctrl+Enter sends.',
  'taulut.lisaaKommentti': 'Add comment',
  'taulut.muokkaaKommenttia': 'Edit comment',
  'taulut.poistaKommentti': 'Delete comment',
  'taulut.poistetaankoKommentti': 'Delete this comment? This cannot be undone.',
  'taulut.muokattu': 'edited',
  'taulut.kommenttiAgentilta': (lahde: string) =>
    `Written by an agent on the author\u2019s behalf (${lahde})`,
  'taulut.monistettu': 'Task duplicated.',
  'taulut.siirretty': 'Task moved.',
  'taulut.toimintoEpaonnistui': 'The action failed.',
  'taulut.ohje':
    'Drag a task to move it to another section or swimlane; hold Ctrl, Alt or Cmd while dragging to duplicate it instead. From the keyboard, open the task and change its swimlane or section. The exclamation mark means the task has been in BLOCKED for more than 8 days.',

  // --- Asiakkaan tiedot: kuvaus, yhteyshenkilöt, linkit (suunnitelma 8) ----
  'asiakastiedot.kuvaus': 'Description',
  'asiakastiedot.kuvausTyhja': 'No description.',
  'asiakastiedot.kuvausVihje': 'Markdown works: headings, lists, bold and links.',
  'asiakastiedot.yhteyshenkilot': 'Contacts',
  'asiakastiedot.eiYhteyshenkiloita': 'No contacts.',
  'asiakastiedot.uusiYhteyshenkilo': 'New contact',
  'asiakastiedot.muokkaaYhteyshenkiloa': 'Edit contact',
  'asiakastiedot.poistaYhteyshenkilo': (nimi: string) => `Delete contact ${nimi}?`,
  'asiakastiedot.nimi': 'Name',
  'asiakastiedot.rooli': 'Role at the customer',
  'asiakastiedot.roolinVihje': 'For example finance manager or technical contact.',
  'asiakastiedot.sahkoposti': 'Email',
  'asiakastiedot.puhelin': 'Phone',
  'asiakastiedot.huomio': 'Note',
  'asiakastiedot.huomionVihje':
    'Availability, area of responsibility or anything worth remembering.',
  'asiakastiedot.ensisijainen': 'Primary contact',
  'asiakastiedot.ensisijainenSelite': ' — replaces the previous choice',
  'asiakastiedot.ensisijainenMerkki': 'Primary',
  'asiakastiedot.linkit': 'Documents',
  'asiakastiedot.eiLinkkeja': 'No document links.',
  'asiakastiedot.uusiLinkki': 'New link',
  'asiakastiedot.muokkaaLinkkia': 'Edit link',
  'asiakastiedot.poistaLinkki': (nimi: string) => `Delete link ${nimi}?`,
  'asiakastiedot.nimilappu': 'Label',
  'asiakastiedot.nimilapunVihje': 'What the document is, for example \u201cContract 2026\u201d.',
  'asiakastiedot.osoite': 'Address',
  'asiakastiedot.osoitteenVihje': 'Only http and https addresses.',
  'asiakastiedot.tagit': 'Tags',
  'asiakastiedot.tagienVihje': 'Enter adds a tag. Suggestions are already in use in the house.',
  'asiakastiedot.lisaaTagi': 'Add tag',
  'asiakastiedot.poistaTagi': (tagi: string) => `Remove tag ${tagi}`,
  'asiakastiedot.tagejaEnintaan': (maara: number) => `At most ${maara} tags.`,
  'asiakastiedot.siirraYlos': 'Move up',
  'asiakastiedot.siirraAlas': 'Move down',
  'asiakastiedot.avaaUudessa': 'Opens in a new tab',
  'asiakastiedot.sisainenEiYhteyshenkiloita':
    'The internal customer has no contacts \u2014 it is a bookkeeping device, not a customer relationship.',
  'asiakastiedot.eiOikeutta':
    'Only an admin and the account manager for this customer can edit these.',
  'asiakastiedot.otsikko': (nimi: string) => `Customer details \u00b7 ${nimi}`,
  'asiakastiedot.avaaTiedot': 'Customer details',

  // --- Development and competence -------------------------------------------
  'navi.kehitys': 'Development',
  'kehitys.otsikko': 'My development',
  'kehitys.selite':
    'Certifications achieved and how long they stay valid. Your manager confirms an achievement, and only confirmed ones appear in the company competence report.',
  'kehitys.esihenkilo': 'Manager',
  'kehitys.eiEsihenkiloa': 'No manager set. Ask an admin to add one.',
  'kehitys.sertifikaatit': 'Certifications',
  'kehitys.eiSertifikaatteja': 'No certifications recorded.',
  'kehitys.kirjaaSaavutus': 'Record a certification',
  'kehitys.sertifikaatti': 'Certification',
  'kehitys.suoritettu': 'Achieved',
  'kehitys.voimassaAsti': 'Valid until',
  'kehitys.eiVanhene': 'Does not expire',
  'kehitys.todistuslinkki': 'Credential link',
  'kehitys.todistuslinkkiVihje':
    'Link to the provider\u2019s credential, for example Credly or Microsoft Learn.',
  'kehitys.huomio': 'Note',
  'kehitys.vanhenemisVihje': 'Leave empty to compute the date from the catalogue validity.',
  'kehitys.omaVanheneminen': 'Set the expiry date myself',
  'kehitys.eiVanheneValinta': 'This achievement does not expire',
  'kehitys.tila.VOIMASSA': 'Valid',
  'kehitys.tila.VANHENEMASSA': 'Expiring',
  'kehitys.tila.VANHENTUNUT': 'Expired',
  'kehitys.odottaaVahvistusta': 'Awaiting confirmation',
  'kehitys.vahvista': 'Confirm',
  'kehitys.vahvistanut': (nimi: string) => `Confirmed by ${nimi}`,
  'kehitys.kirjannut': (nimi: string) => `Recorded by ${nimi}`,
  'kehitys.alaiset': 'Direct reports',
  'kehitys.alaisetSelite': 'People whose development discussions you hold.',
  'kehitys.voimassaMaara': (maara: number) => `${maara} valid`,
  'kehitys.odottaaMaara': (maara: number) => `${maara} awaiting confirmation`,
  'kehitys.vanheneeMaara': (maara: number) => `${maara} expiring`,
  'kehitys.avaaHenkilo': 'Open',
  'kehitys.paluuOmaan': 'Back to my development',
  'kehitys.henkilonOtsikko': (nimi: string) => `Development \u00b7 ${nimi}`,
  'kehitys.passivoitu': 'deactivated',
  'kehitys.eiVoiVahvistaaItse': 'You cannot confirm your own achievement.',

  'osaaminen.otsikko': 'Competence',
  'osaaminen.luettelo': 'Certification catalogue',
  'osaaminen.luetteloSelite':
    'What can be achieved here. A row is deactivated, never deleted \u2014 it may have achievements.',
  'osaaminen.matriisi': 'Competence matrix',
  'osaaminen.matriisiSelite':
    'Confirmed achievements per person. This is the view opened for a tender attachment.',
  'osaaminen.uusiSertifikaatti': 'New certification',
  'osaaminen.koodi': 'Code',
  'osaaminen.nimi': 'Name',
  'osaaminen.tarjoaja': 'Provider',
  'osaaminen.voimassaoloKuukautta': 'Validity (months)',
  'osaaminen.osoite': 'URL',
  'osaaminen.saavutuksia': 'Achievements',
  'osaaminen.aktiivinen': 'Active',
  'osaaminen.passivoi': 'Deactivate',
  'osaaminen.otaKayttoon': 'Reactivate',
  'osaaminen.eiSertifikaatteja': 'The catalogue is empty. Add the first certification.',
  'osaaminen.eiSaavutuksia': 'No confirmed achievements yet.',
  'osaaminen.kaikkiTarjoajat': 'All providers',
  'osaaminen.kaikkiSertifikaatit': 'All certifications',
  'osaaminen.vainVoimassa': 'Valid only',
  'osaaminen.myosPassivoidut': 'Include deactivated people',
  'osaaminen.vieCsv': 'Export CSV',
  'osaaminen.voimassaYhteensa': (maara: number) => `${maara} people hold this`,
  'osaaminen.koodiVihje': 'The provider\u2019s identifier, for example AZ-104 or SAA-C03.',
  'osaaminen.voimassaoloVihje': 'Empty = does not expire.',
  'osaaminen.koodista': 'from code',
  'osaaminen.koodistaVihje':
    'These details come from the development path catalogue in code. Change them there and run pnpm polut:sync — here the row can only be deactivated.',
  'osaaminen.koodiaEiVoiMuuttaa':
    'The code cannot be changed. Fix a wrong code by deactivating the row and creating the right one.',

  // --- Kehityspolut (docs/KEHITYSSUUNNITELMAT.md, luku 16) -----------------
  'polut.otsikko': 'Development paths',
  'polut.selite':
    'Ready-made paths to certification: what to do first, what it requires and how long it takes. Browsing commits you to nothing.',
  'polut.eiPolkuja': 'No paths have been defined yet.',
  'polut.paluuKehitykseen': 'Back to my development',
  'polut.kaikkiPolut': 'All development paths',
  'polut.eiLoytynyt': 'Path not found. It may have been removed from the catalogue.',
  'polut.selaaPolkuja': 'Browse development paths',
  'polut.askelia': (maara: number) => `${maara} steps`,
  'polut.askeleet': 'Steps',
  'polut.kuormitus': 'Estimated effort',
  'polut.omaEteneminen': 'My progress',
  'polut.tunnit': (tunnit: number) => `about ${tunnit} h`,
  'polut.tunnitYhteensa': (tunnit: number) => `about ${tunnit} h in total`,
  'polut.eteneminen': (tehty: number, kaikki: number) => `${tehty}/${kaikki} completed`,
  'polut.rooli.REQUIRED': 'Core',
  'polut.rooli.RECOMMENDED': 'Recommended',
  'polut.rooli.OPTIONAL': 'Next step',
  'polut.tila.SUORITETTU': 'Completed',
  'polut.tila.TAVOITTEENA': 'A goal',
  'polut.tila.EI_ALOITETTU': 'Not started',
  'polut.tenttiversio': (koodi: string) => `exam ${koodi}`,
  'polut.tavoiteOtsikko': (koodi: string) => `${koodi} completed`,
  'polut.tenttimaksu': (maara: number, valuutta: string) => `exam ${maara} ${valuutta}`,
  'polut.hintaTarjoajalta': 'price on the provider site',
  'polut.tarjoajanSivu': 'Provider page',
  'polut.edellyttaa': (nimet: string) => `Requires: ${nimet}`,
  'polut.asetaTavoitteeksi': 'Set as a goal',
  'polut.valmisSertifikaatti': (koodi: string) => `${koodi} exam passed and credential received.`,
  'polut.valmisKoulutus': (nimi: string) => `Training “${nimi}” completed.`,
  'polut.valmisMateriaali': (nimi: string) => `Material “${nimi}” worked through.`,
  'polut.linkkiVihje':
    'Requirements, exam topics and prices live on the provider sites — that is where they stay current. This page carries the order, the reasoning and the time estimate.',
  'polut.yllapitoVihje':
    'Paths are maintained in code so that every change is reviewable. Tell an admin if a step is out of date or a path is missing.',

  'kayttajat.esihenkilo': 'Manager',
  'kayttajat.eiEsihenkiloa': 'No manager',
  'kayttajat.esihenkiloVihje':
    'The manager sees this person\u2019s development plan and receives the discussion reminder.',

  // --- Development plan -----------------------------------------------------
  'kehitys.suunta': 'Direction',
  'kehitys.suuntaVihje': 'The longer-term direction that goals sit under. Written by the manager.',
  'kehitys.eiSuuntaa': 'No direction recorded.',
  'kehitys.muokkaaSuuntaa': 'Edit direction',
  'kehitys.tavoitteet': 'Goals',
  'kehitys.eiTavoitteita': 'No goals yet. Add the first one.',
  'kehitys.uusiTavoite': 'New goal',
  'kehitys.muokkaaTavoitetta': 'Edit goal',
  'kehitys.tavoiteOtsikko': 'Goal',
  'kehitys.tavoiteOtsikkoVihje': 'For example \u201cAZ-104 passed\u201d.',
  'kehitys.valmiinMaaritelma': 'How will we know this is done?',
  'kehitys.valmiinMaaritelmaVihje':
    'Without this, the discussion six months from now is a discussion about a feeling.',
  'kehitys.tavoitepaiva': 'Target date',
  'kehitys.kuvaus': 'Description',
  'kehitys.kohde': 'Linked to',
  'kehitys.eiKohdetta': 'No certification or training',
  'kehitys.kohdeSertifikaatti': 'Certification',
  'kehitys.kohdeKoulutus': 'Internal training',
  'kehitys.tavoiteTila.PLANNED': 'Planned',
  'kehitys.tavoiteTila.IN_PROGRESS': 'In progress',
  'kehitys.tavoiteTila.ACHIEVED': 'Achieved',
  'kehitys.tavoiteTila.DROPPED': 'Dropped',
  'kehitys.myohassa': 'Overdue',
  'kehitys.merkitseSaavutetuksi': 'Mark achieved',
  'kehitys.hylkaa': 'Drop',
  'kehitys.hylkaysSyy': 'Why is this goal dropped?',
  'kehitys.poistaTavoite': 'Delete',
  'kehitys.saavutettu': (paiva: string) => `Achieved ${paiva}`,
  'kehitys.edellinenKeskustelu': (paiva: string) => `Previous discussion ${paiva}`,
  'kehitys.eiKeskusteltu': 'Not discussed yet.',

  'kehitys.keskustelut': 'Discussions',
  'kehitys.eiKeskusteluja': 'No development discussions held yet.',
  'kehitys.aloitaKeskustelu': 'Start a discussion',
  'kehitys.jatkaKeskustelua': 'Continue discussion',
  'kehitys.avoinKeskustelu': 'Open discussion',
  'kehitys.keskustelunPaiva': 'Discussion date',
  'kehitys.seuraavaKeskustelu': 'Next discussion',
  'kehitys.seuraavaEraantyy': (paiva: string) => `Next discussion ${paiva}`,
  'kehitys.seuraavaEraantynyt': (paiva: string) => `Next discussion was due ${paiva}`,
  'kehitys.yhteenveto': 'What was agreed',
  'kehitys.yhteenvetoVihje': 'Visible to the employee. Markdown supported.',
  'kehitys.suljeKeskustelu': 'Close discussion',
  'kehitys.suljettu': (paiva: string) => `Closed ${paiva}`,
  'kehitys.suljettuVaroitus':
    'A closed discussion can no longer be changed. Record corrections in the next one.',
  'kehitys.poistaKeskustelu': 'Delete discussion',
  'kehitys.puuttuuKirjauksia': (maara: number) =>
    maara === 1 ? '1 goal without an entry' : `${maara} goals without an entry`,
  'kehitys.kaikkiKirjattu': 'All goals recorded.',
  'kehitys.tamanKeskustelunKirjaus': 'This discussion',
  'kehitys.valitseTavoite': 'Pick a goal on the left.',
  'kehitys.kommentti': 'What was said about this?',
  'kehitys.suljetutTavoitteet': 'Completed and dropped',
  'kehitys.kirjattu': 'Recorded',
  'kehitys.tavoitteitaKasitelty': (maara: number) => `${maara} goals`,
  'kehitys.keskusteluaEiLoytynyt': 'Discussion not found.',
  'kehitys.avaaKeskustelu': 'Open',
  'kehitys.avoimiaTavoitteita': (maara: number) => `${maara} goals open`,
  'kehitys.myohassaMaara': (maara: number) => `${maara} overdue`,
  'kehitys.keskusteluEraantynyt': 'Discussion due',
  'kehitys.paluuSuunnitelmaan': 'Back to the plan',
  'kehitys.vainEsihenkilo': 'The manager starts and closes the discussion.',
  'kehitysKatsaus.otsikko': 'Development discussions',
  'kehitysKatsaus.selite':
    'Who has a manager, when they last talked, and what is overdue. No discussion content here.',
  'kehitysKatsaus.henkilo': 'Person',
  'kehitysKatsaus.esihenkilo': 'Manager',
  'kehitysKatsaus.viimeksi': 'Last',
  'kehitysKatsaus.seuraava': 'Next',
  'kehitysKatsaus.tavoitteet': 'Goals',
  'kehitysKatsaus.sertifikaatit': 'Certifications',
  'kehitysKatsaus.eiEsihenkiloa': 'No manager',
  'kehitysKatsaus.eiKeskusteltu': 'Never',
  'kehitysKatsaus.eraantynyt': 'Due',
  'kehitysKatsaus.avoinKeskustelu': 'Discussion in progress',
  'kehitysKatsaus.henkiloita': 'People',
  'kehitysKatsaus.ilmanEsihenkiloa': 'Without a manager',
  'kehitysKatsaus.eraantyneita': 'Due',
  'kehitysKatsaus.eiKoskaanKeskusteltu': 'Never discussed',
  'kehitysKatsaus.tyhja': 'No people to track.',
  'asetukset.sailytysaika': 'Development discussion retention',
  'asetukset.sailytysaikaVihje':
    'Months from account deactivation. The competence registry is not removed. 0 = no automatic deletion.',
  'asetukset.kuukautta': 'months',

  // --- Secret sharing (docs/SALAISUUDET.md) ------------------------------
  'navi.salaisuudet': 'Secrets',
  'salaisuudet.otsikko': 'Secret sharing',
  'salaisuudet.selite':
    'Share a password, key or other text as a link that can be opened only once. After opening, the content is destroyed, and you see when and from which address it was opened.',
  'salaisuudet.uusi': 'New secret',
  'salaisuudet.omat': 'Mine',
  'salaisuudet.kaikki': 'Everyone’s',
  'salaisuudet.rajaus': 'Secrets shown',
  'salaisuudet.kaikkiTilat': 'All states',
  'salaisuudet.nimi': 'Name',
  'salaisuudet.jakaja': 'Shared by',
  'salaisuudet.luotu': 'Created',
  'salaisuudet.vanhenee': 'Expires',
  'salaisuudet.tila': 'State',
  'salaisuudet.avattu': 'Opened',
  'salaisuudet.lukija': 'Opened by',
  'salaisuudet.pinKaytossa': 'Protected with a PIN',
  'salaisuudet.pinYrityksia': (maara: number) =>
    maara === 1 ? '1 wrong PIN attempt' : `${maara} wrong PIN attempts`,
  'salaisuudet.eiSalaisuuksia': 'No shared secrets.',
  'salaisuudet.peru': 'Revoke secret',
  'salaisuudet.peruVahvistus': (nimi: string) =>
    `Revoke the secret “${nimi}”? The content is destroyed immediately and the link stops working.`,
  'salaisuudet.perunut': (nimi: string) => `Revoked by ${nimi}`,
  'salaisuudet.tila.PENDING': 'Waiting',
  'salaisuudet.tila.READ': 'Opened',
  'salaisuudet.tila.EXPIRED': 'Expired',
  'salaisuudet.tila.REVOKED': 'Revoked',
  'salaisuudet.tila.DESTROYED': 'Destroyed',
  'salaisuudet.suljettu': (aika: string) => `Closed ${aika}`,
  'salaisuudet.kopioi': 'Copy',
  'salaisuudet.kopioitu': 'Copied',
  'salaisuudet.lomake.nimi': 'Name',
  'salaisuudet.lomake.nimiEsimerkki': 'E.g. Customer Ltd – production database admin',
  'salaisuudet.lomake.nimiVihje': 'Only for you. The recipient does not see the name.',
  'salaisuudet.lomake.sisalto': 'Secret',
  'salaisuudet.lomake.merkkeja': (maara: string, enintaan: string) =>
    `${maara} / ${enintaan} characters`,
  'salaisuudet.lomake.vanhenee': 'Expires',
  'salaisuudet.lomake.muu': 'Other…',
  'salaisuudet.lomake.vanheneeVihje': (aika: string) =>
    `The link expires ${aika} unless it is opened before that.`,
  'salaisuudet.lomake.vanheneeVirhe': 'Choose a time between 15 minutes and 30 days from now.',
  'salaisuudet.ttl.1h': '1 hour',
  'salaisuudet.ttl.1d': '1 day',
  'salaisuudet.ttl.3d': '3 days',
  'salaisuudet.ttl.7d': '7 days',
  'salaisuudet.ttl.14d': '14 days',
  'salaisuudet.ttl.30d': '30 days',
  'salaisuudet.lomake.pin': 'Protect with a PIN',
  'salaisuudet.lomake.pinVihje':
    'The recipient needs the PIN in addition to the link. Five wrong attempts destroy the secret.',
  'salaisuudet.lomake.pinArvo': 'Generate',
  'salaisuudet.lomake.pinOma': 'Custom',
  'salaisuudet.lomake.pinKentta': 'PIN (4–6 digits)',
  'salaisuudet.lomake.pinMuoto': 'The PIN has 4–6 digits.',
  'salaisuudet.lomake.pinTriviaali': 'The PIN is too easy to guess.',
  'salaisuudet.lomake.luo': 'Create link',
  'salaisuudet.lomake.luodaan': 'Creating…',
  'salaisuudet.valmis.otsikko': 'The link is ready',
  'salaisuudet.valmis.kertaalleen':
    'The link is shown only now. If you lose it, revoke the secret and create a new one.',
  'salaisuudet.valmis.linkki': 'Link',
  'salaisuudet.valmis.pin': 'PIN',
  'salaisuudet.valmis.pinOhje':
    'Send the PIN through a different channel than the link, for example by text message or phone.',
  'salaisuudet.valmis.sulje': 'Done',

  'salaisuus.otsikko': 'Shared secret',
  'salaisuus.jaettu': (kuka: string, yritys: string) =>
    `${kuka} (${yritys}) has shared a secret with you.`,
  'salaisuus.kerran':
    'The secret can be opened only once. After opening, it is permanently deleted from the server.',
  'salaisuus.vanhenee': (aika: string) => `The link expires ${aika}.`,
  'salaisuus.pin': 'PIN',
  'salaisuus.pinVihje': 'The sender has given you the PIN separately.',
  'salaisuus.nayta': 'Show secret',
  'salaisuus.avataan': 'Opening…',
  'salaisuus.poistettu':
    'The secret has now been deleted from the server. Save it before you close this page.',
  'salaisuus.naytaSisalto': 'Show content',
  'salaisuus.piilotaSisalto': 'Hide content',
  'salaisuus.kopioi': 'Copy',
  'salaisuus.kopioitu': 'Copied',
  'salaisuus.vaaraPin': (jaljella: number) =>
    jaljella === 1
      ? 'Wrong PIN. One attempt left; after that the secret is destroyed.'
      : `Wrong PIN. Attempts left: ${jaljella}.`,
  'salaisuus.pinPuuttuu': 'Enter the PIN.',
  'salaisuus.joAvattu': (aika: string) => `This secret was already opened ${aika}.`,
  'salaisuus.joAvattuVaroitus': 'If you did not open it yourself, contact the sender immediately.',
  'salaisuus.vanhentunut': (aika: string) => `The secret expired unopened ${aika}.`,
  'salaisuus.peruttu': (aika: string) => `The sender revoked the secret ${aika}.`,
  'salaisuus.tuhottu': 'The secret was destroyed because the PIN was entered wrong too many times.',
  'salaisuus.pyydaUusi': 'Ask the sender for a new link.',
  'salaisuus.eiLoydy': 'The link is invalid or the secret has been deleted.',
  'salaisuus.katkennut':
    'The link is broken. Copy the whole link, including the part after the # sign, and open it again.',
  'salaisuus.rajoitin': 'Too many requests. Wait a minute and try again.',
  'salaisuus.virhe': 'Opening the secret failed. Please try again.',
  'salaisuus.rajapinta': 'Read via the API',
  'salaisuus.rajapintaOhje':
    'The command opens the secret just like the button — only once. The response is JSON with the secret in the content field.',
  'salaisuus.kieli': 'Language',
};
